import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Icon } from '@iconify/react';
import {
  getPaginatedShiftLogs,
  getShiftLogById,
  createShiftLog,
  updateShiftLog,
  getShiftLogDepartments,
  getDepartmentChildren,
  getDowntimeComments,
} from '../api/shift-logAPI';
import { getUserses } from '../api/usersAPI';
import { getTechnicians } from '../api/techniciansAPI';
import {
  getEventsById,
  getPaginatedEvents,
  createEvents,
  updateEvents,
} from '../api/eventsAPI';
import {
  createShiftLogTask,
  deleteShiftLogTask,
  getShiftLogTasks,
  updateShiftLogTask,
} from '../api/shift-log-tasksAPI';
import { getEventStatus } from '../api/event-statusAPI';
import { getEventTypes } from '../api/event-typeAPI';
import { getEventResponsibilities } from '../api/event-responsibilityAPI';
import { getPaginatedAssets } from '../api/assetAPI';
import {
  createEventDelay,
  deleteEventDelay,
  getEventDelayByEventId,
  updateEventDelay,
} from '../api/event-delayAPI';
import { createEventComment } from '../api/event-commentsAPI';
import { getEventDelayCodes } from '../api/event-delay-codeAPI';
import { getPaginatedWorkOrders, updateWorkOrder } from '../api/work-orderAPI';
import { getWorkOrderStatus } from '../api/work-order-statusAPI';
import { getDailyAISummary } from '../api/ai-summaryAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useValidator } from '../utils/useValidator';
import { useSetting } from '../utils/AppSettingsContext';
import { toDateInputValue } from '../shared/dates';
import { computeDefaultDelayStartDate, validateDelayDateRange } from '../utils/eventDelayRules';
import { formatTimeSincePosting } from '../utils/timeSincePosting';
import {
  getEventRange,
  hasWorkOrdersOutsideEventRange,
  buildCompleteWorkOrderPayload,
  areWorkOrdersCompletableForEvent,
} from '../utils/eventWorkOrderRules';
import CreateDisplayOnlyUserModal from './child/CreateDisplayOnlyUserModal';
import EventEditModal from './EventEditModal';

const SHIFT_NAME_OPTIONS = Object.freeze([
  { value: 'Day Shift', label: 'Day Shift' },
  { value: 'Night Shift', label: 'Night Shift' },
]);

const getDefaultShiftDate = () => toDateInputValue(new Date());

const createInitialShiftLogForm = () => ({
  userId: '',
  departmentId: '',
  shiftDate: getDefaultShiftDate(),
  shiftName: SHIFT_NAME_OPTIONS[0]?.value ?? '',
  crew: [],
  contractorList: [],
  notes: '',
  safteyNotes: '',
  completed: 0,
  reportStartDate: null,
  reportEndDate: null,
  reportStartDateDate: '',
  reportStartDateTime: '',
  reportEndDateDate: '',
  reportEndDateTime: '',
  files: [],
  downtimeList: [],
  upcomingEvents: [],
});

const normalizeDateForPayload = (value) => {
  const normalized = toDateInputValue(value);
  return normalized || null;
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.warn('Failed to parse array value', err);
      return [];
    }
  }
  return [];
};

const normalizeFileEntry = (file) => {
  if (!file) return null;
  if (typeof file === 'string') {
    const name = file.split('/').pop() || file;
    return {
      name,
      key: file,
      filename: file,
    };
  }
  if (typeof file === 'object') {
    const key = file.key || file.filename || file.url || file.name || '';
    const name = file.name || (key ? key.split('/').pop() : 'File');
    const normalized = {
      ...file,
      name,
      key,
    };
    if (!normalized.filename && key) {
      normalized.filename = key;
    }
    if (normalized.size !== undefined && typeof normalized.size !== 'number') {
      delete normalized.size;
    }
    return normalized;
  }
  return null;
};

const parseFileList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(normalizeFileEntry).filter(Boolean);
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeFileEntry).filter(Boolean);
      }
    } catch (err) {
      console.warn('Failed to parse file list', err);
    }
  }
  return [];
};

const ADD_CREW_MEMBER_OPTION = Object.freeze({
  value: '__add_new__',
  label: '+ Add New Crew Member',
  isAddNew: true,
});

const serializeFilesForPayload = (files) => {
  if (!Array.isArray(files)) return [];
  return files
    .map((file) => {
      if (!file) return null;
      const key = file.key || file.filename || (typeof file === 'string' ? file : null);
      if (!key) return null;
      const name = file.name || key.split('/').pop() || key;
      const size = typeof file.size === 'number' ? file.size : undefined;
      const url = typeof file.url === 'string' ? file.url : undefined;
      const payload = {
        name,
        key,
        filename: file.filename || key,
      };
      if (size !== undefined) payload.size = size;
      if (url) payload.url = url;
      return payload;
    })
    .filter(Boolean);
};

const formatEventResponsibilityValue = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return (
      value.name
      || value.label
      || value.title
      || (value.id !== undefined && value.id !== null ? String(value.id) : null)
    );
  }
  return null;
};

const formatEventCommentsValue = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const normalizedComments = value
      .map((item) => {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (typeof item === 'object') {
          return item.comment || item.comments || item.text || item.content || null;
        }
        return null;
      })
      .filter(Boolean);
    return normalizedComments.length ? normalizedComments.join('\n') : null;
  }
  if (typeof value === 'object') {
    return value.comment || value.comments || value.text || value.content || null;
  }
  return null;
};

const normalizeShiftLogEventForPayload = (eventItem) => {
  if (!eventItem || typeof eventItem !== 'object') return eventItem;
  const normalized = { ...eventItem };

  const responsibilityValue =
    formatEventResponsibilityValue(normalized.eventResponsibility)
    || formatEventResponsibilityValue(normalized.eventResponsibilityName)
    || formatEventResponsibilityValue(normalized.eventResponsibilityLabel)
    || formatEventResponsibilityValue(normalized['Event Responsibility'])
    || formatEventResponsibilityValue(normalized.tblEventResponsibility);

  if (responsibilityValue) {
    normalized.eventResponsibility = responsibilityValue;
    normalized['Event Responsibility'] = responsibilityValue;
  }

  const commentsValue =
    formatEventCommentsValue(normalized.tblEventComments)
    || formatEventCommentsValue(normalized.eventComments)
    || formatEventCommentsValue(normalized.comments)
    || formatEventCommentsValue(normalized['Tbl Event Comments']);

  if (commentsValue) {
    normalized.tblEventComments = commentsValue;
    normalized.eventComments = commentsValue;
    normalized['Tbl Event Comments'] = commentsValue;
  }

  return normalized;
};

const buildFileUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string') {
    return `${process.env.REACT_APP_SPACES_BASE}/${file}`;
  }
  if (file.url) return file.url;
  const key = file.key || file.filename;
  if (!key) return '';
  return `${process.env.REACT_APP_SPACES_BASE}/${key}`;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
};

const toLocalInputFormat = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatMinutes = (value) => String(value).padStart(2, '0');

const parseTimeToMinutes = (value) => {
  if (!value && value !== 0) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parts = raw.split(':').map((part) => Number.parseInt(part, 10));
  if (parts.some((part) => Number.isNaN(part))) return null;
  const [hours = 0, minutes = 0, seconds = 0] = parts;
  if (hours < 0 || minutes < 0 || seconds < 0) return null;
  return hours * 60 + minutes + Math.round(seconds / 60);
};

const parseDurationToMinutes = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value * 60);
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.includes(':')) {
    return parseTimeToMinutes(raw);
  }
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) {
    return Math.round(numeric * 60);
  }
  return null;
};

const formatMinutesToTimeString = (minutes) => {
  if (!Number.isFinite(minutes)) return '';
  const total = ((minutes % 1440) + 1440) % 1440;
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${formatMinutes(hours)}:${formatMinutes(mins)}`;
};

const splitDateTimeParts = (value) => {
  if (!value) return { date: '', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: '', time: '' };
  }
  return {
    date: toDateInputValue(date),
    time: `${formatMinutes(date.getHours())}:${formatMinutes(date.getMinutes())}`,
  };
};

const buildIsoFromParts = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;
  const [hours, minutes] = String(timeValue)
    .split(':')
    .map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

const addMinutesToIso = (isoValue, minutes) => {
  if (!isoValue || !Number.isFinite(minutes)) return null;
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return null;
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

const formatDateTimeDisplay = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = formatMinutes(date.getDate());
  const monthLabels = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const month = monthLabels[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  const hours = formatMinutes(date.getHours());
  const minutes = formatMinutes(date.getMinutes());
  if (!month) return '—';
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const formatReportRange = (reportStartDate, reportEndDate) => {
  const startLabel = formatDateTimeDisplay(reportStartDate);
  const endLabel = formatDateTimeDisplay(reportEndDate);
  if (startLabel === '—' && endLabel === '—') return '—';
  if (startLabel === '—') return endLabel;
  if (endLabel === '—') return startLabel;
  return `${startLabel} - ${endLabel}`;
};

const calcDelayDuration = (delay) =>
  delay?.delayEndDate && delay?.delayStartDate
    ? (new Date(delay.delayEndDate) - new Date(delay.delayStartDate)) / (1000 * 60 * 60)
    : 0;

const buildDelaySummary = (event) => {
  const rawDelays = Array.isArray(event?.tblEventDelays)
    ? event.tblEventDelays
    : Array.isArray(event?.eventDelay)
      ? event.eventDelay
      : event?.eventDelay
        ? [event.eventDelay]
        : [];

  if (!rawDelays.length) return '—';
  return rawDelays
    .map((delay) => {
      if (!delay) return null;
      const name = delay?.eventDelayCode?.name || delay?.eventDelayCodeName || 'Delay';
      const duration = delay?.duration;
      return duration ? `${name} (${duration})` : name;
    })
    .filter(Boolean)
    .join(', ');
};

const ShiftLogManager = ({
  show,
  onClose,
  userProfile,
  initialShiftLogId = null,
  initialReadOnly = false,
  selectedWorkOrder = null,
  onWorkOrdersUpdated = null,
  onWorkOrderLinked = null,
}) => {

  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingFinaliseAction, setPendingFinaliseAction] = useState(null);
  const [activeFormTab, setActiveFormTab] = useState('shiftLog');
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [openLogsLoading, setOpenLogsLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finalisingAction, setFinalisingAction] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [pendingAutoSaveNew, setPendingAutoSaveNew] = useState(false);
  const [shiftNameTouched, setShiftNameTouched] = useState(false);
  const [reportEndDateTouched, setReportEndDateTouched] = useState(false);

  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentMap, setDepartmentMap] = useState({});
  const departmentChildrenCacheRef = useRef(new Map());
  const [defaultDepartmentId, setDefaultDepartmentId] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [userLookup, setUserLookup] = useState(new Map());
  const [crewOptions, setCrewOptions] = useState([ADD_CREW_MEMBER_OPTION]);

  const [openShiftLogs, setOpenShiftLogs] = useState([]);
  const [openLogsLoaded, setOpenLogsLoaded] = useState(false);
  const [departmentForm, setDepartmentForm] = useState({ departmentId: '' });
  const [shiftLogForm, setShiftLogForm] = useState(() => createInitialShiftLogForm());
  const [isNewShiftLog, setIsNewShiftLog] = useState(true);
  const [activeShiftLogId, setActiveShiftLogId] = useState(null);
  const initialShiftLogRef = useRef(null);
  const initialReadOnlyRef = useRef(false);
  const savedSnapshotRef = useRef(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  const [crewSelectOption, setCrewSelectOption] = useState(null);
  const [showCreateCrewModal, setShowCreateCrewModal] = useState(false);
  const [contractorInput, setContractorInput] = useState('');
  const [downTimeEvents, setDownTimeEvents] = useState([]);
  const [inProgressEvents, setInProgressEvents] = useState([]);
  const [recentlyCompletedEvents, setRecentlyCompletedEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [shiftLogTasks, setShiftLogTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ name: '', notes: '' });
  const [taskFormErrors, setTaskFormErrors] = useState({});
  const [taskSaving, setTaskSaving] = useState(false);
  const [updatingTaskIds, setUpdatingTaskIds] = useState(() => new Set());
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({});
  const [eventComments, setEventComments] = useState([]);
  const [eventStatusOptions, setEventStatusOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [eventResponsibilityOptions, setEventResponsibilityOptions] = useState([]);
  const [defaultEventResponsibilityId, setDefaultEventResponsibilityId] = useState(null);
  const [eventAssetOptions, setEventAssetOptions] = useState([]);
  const [eventAssignedWorkOrders, setEventAssignedWorkOrders] = useState([]);
  const [eventWorkOrderStatuses, setEventWorkOrderStatuses] = useState([]);
  const [eventAvailableWorkOrders, setEventAvailableWorkOrders] = useState([]);
  const [showEventAssignModal, setShowEventAssignModal] = useState(false);
  const [eventAssignOptions, setEventAssignOptions] = useState([]);
  const [eventAssignSelected, setEventAssignSelected] = useState([]);
  const [eventAssignSearch, setEventAssignSearch] = useState('');
  const [eventDelays, setEventDelays] = useState([]);
  const [delayCodes, setDelayCodes] = useState([]);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [editingDelay, setEditingDelay] = useState(null);
  const [delayForm, setDelayForm] = useState({
    eventDelayCodeId: '',
    delayStartDate: '',
    delayEndDate: '',
  });
  const delayValidationFields = useMemo(
    () => [
      { field: 'eventDelayCodeId', label: 'Delay Code', required: true },
      { field: 'delayStartDate', label: 'Delay Start Date', required: true, type: 'datetime' },
      { field: 'delayEndDate', label: 'Delay End Date', required: true, type: 'datetime' },
    ],
    [],
  );
  const delayFields = useMemo(
    () => [
      {
        field: 'eventDelayCodeId',
        label: 'Delay Code',
        type: 'select',
        size: 12,
        required: true,
        options: (delayCodes || []).map((code) => ({
          value: code.id,
          label: [code.code, code.name].filter(Boolean).join(' - '),
        })),
      },
      {
        field: 'delayStartDate',
        label: 'Start Date',
        type: 'datetime',
        size: 12,
        required: true,
      },
      {
        field: 'delayEndDate',
        label: 'End Date',
        type: 'datetime',
        size: 12,
        required: true,
      },
    ],
    [delayCodes],
  );

  const {
    validate: validateDelay,
    errors: delayErrors,
    setErrors: setDelayErrors,
  } = useValidator(delayValidationFields, delayForm);

  const { renderField: renderDelayField } = useFormBuilder(
    delayFields,
    delayForm,
    setDelayForm,
    delayErrors,
  );
  const [delayDuration, setDelayDuration] = useState(0);
  const filteredEventAssignOptions = useMemo(() => {
    if (!eventAssignSearch) return eventAssignOptions;
    const searchLower = eventAssignSearch.toLowerCase();
    return eventAssignOptions.filter(
      (wo) =>
        (wo.workOrderNumber || '').toLowerCase().includes(searchLower) ||
        (wo.workOrderTitle || '').toLowerCase().includes(searchLower),
    );
  }, [eventAssignOptions, eventAssignSearch]);


  const [downtimeComments, setDowntimeComments] = useState([]);
  const [downtimeCommentsLoading, setDowntimeCommentsLoading] = useState(false);
  const [downtimeCommentsError, setDowntimeCommentsError] = useState(null);
  const [collapsedCommentEventIds, setCollapsedCommentEventIds] = useState(() => new Set());

  const shiftStartTimeSetting = useSetting('shift_start_time', '08:00:00');
  const shiftDurationSetting = useSetting('shift_duration', 8);
  const primaryNotesLabel = useSetting('shift_log_primary_label', 'General Notes');
  const secondaryNotesLabel = useSetting('shift_log_secondary_label', 'Safety Notes');

  const shiftStartMinutes = useMemo(
    () => parseTimeToMinutes(shiftStartTimeSetting) ?? 8 * 60,
    [shiftStartTimeSetting],
  );
  const shiftDurationMinutes = useMemo(
    () => parseDurationToMinutes(shiftDurationSetting) ?? 8 * 60,
    [shiftDurationSetting],
  );

  const eventTechsSetting = useSetting('event_techs');
  const completeEventWorkOrdersSetting = useSetting('complete_event_work_orders', false);
  const eventTechsEnabled = Number(eventTechsSetting) === 1;
  const pendingEventStatusId = useMemo(() => {
    const pending = eventStatusOptions.find(
      (status) => status?.label && status.label.toLowerCase() === 'pending',
    );
    return pending?.value ?? null;
  }, [eventStatusOptions]);

  const reportStartDateValue = shiftLogForm?.reportStartDate;
  const formattedReportStartDate = useMemo(() => {
    if (!reportStartDateValue) {
      return 'this shift';
    }
    const date = new Date(reportStartDateValue);
    if (Number.isNaN(date.getTime())) {
      return reportStartDateValue;
    }
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [reportStartDateValue]);

  const getShiftNameForTime = useCallback((timeValue) => {
    const minutes = parseTimeToMinutes(timeValue);
    if (!Number.isFinite(minutes)) return '';
    if (minutes >= 16 * 60 || minutes < 4 * 60) {
      return 'Night Shift';
    }
    if (minutes >= 4 * 60 && minutes < 16 * 60) {
      return 'Day Shift';
    }
    return '';
  }, []);

  const buildDefaultReportDates = useCallback(() => {
    const now = new Date();
    const today = toDateInputValue(now);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const shiftEndMinutes = shiftStartMinutes + shiftDurationMinutes;
    let selectedMinutes = shiftStartMinutes;
    if (nowMinutes > shiftStartMinutes && nowMinutes < shiftEndMinutes) {
      selectedMinutes = shiftStartMinutes;
    } else if (nowMinutes > shiftEndMinutes) {
      selectedMinutes = shiftEndMinutes;
    }
    const startDate = today ? new Date(`${today}T00:00:00`) : new Date(now);
    if (!Number.isNaN(startDate.getTime())) {
      startDate.setHours(0, 0, 0, 0);
      startDate.setMinutes(selectedMinutes);
    }
    const reportStartDate = startDate.toISOString();
    const reportEndDate = addMinutesToIso(reportStartDate, shiftDurationMinutes);
    const startParts = splitDateTimeParts(reportStartDate);
    const endParts = splitDateTimeParts(reportEndDate);
    return {
      reportStartDate,
      reportEndDate,
      reportStartDateDate: startParts.date,
      reportStartDateTime: startParts.time,
      reportEndDateDate: endParts.date,
      reportEndDateTime: endParts.time,
    };
  }, [shiftDurationMinutes, shiftStartMinutes]);

  const downtimeEventsWithIds = useMemo(() => {
    if (!Array.isArray(downTimeEvents)) {
      return [];
    }
    return downTimeEvents.filter((event) => {
      const id = Number(event?.id ?? event?.eventId);
      return Number.isFinite(id) && id > 0;
    });
  }, [downTimeEvents]);

  const downtimeEventsById = useMemo(() => {
    const map = new Map();
    downtimeEventsWithIds.forEach((event) => {
      const id = Number(event?.id ?? event?.eventId);
      if (Number.isFinite(id) && id > 0) {
        map.set(id, event);
      }
    });
    return map;
  }, [downtimeEventsWithIds]);

  const eventsWithDowntimeComments = useMemo(() => {
    const groups = Array.isArray(downtimeComments) ? downtimeComments : [];
    return groups
      .map((group) => {
        if (!group) return null;
        const eventId = Number(group.eventId);
        if (!Number.isFinite(eventId) || eventId <= 0) {
          return null;
        }
        const comments = Array.isArray(group.comments)
          ? group.comments.filter(Boolean)
          : [];
        if (!comments.length) {
          return null;
        }
        const eventDetails = downtimeEventsById.get(eventId);
        const eventName =
          eventDetails?.name ||
          eventDetails?.eventName ||
          group?.eventName ||
          `Event ${eventId}`;
        const assetNumber =
          eventDetails?.asset?.assetNumber ??
          eventDetails?.assetNumber ??
          group?.assetNumber ??
          '';
        const assetDescription =
          eventDetails?.asset?.assetDescription ??
          eventDetails?.assetDescription ??
          group?.assetDescription ??
          '';
        const assetLabel = [assetNumber, assetDescription]
          .map((value) => (value ? String(value).trim() : ''))
          .filter(Boolean)
          .join(' - ');
        return {
          eventId,
          eventName,
          assetLabel,
          comments,
        };
      })
      .filter(Boolean);
  }, [downtimeComments, downtimeEventsById]);

  useEffect(() => {
    setCollapsedCommentEventIds((prev) => {
      if (!(prev instanceof Set) || prev.size === 0) {
        return prev;
      }
      const validIds = new Set(eventsWithDowntimeComments.map((event) => event.eventId));
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        }
      });
      if (next.size === prev.size) {
        let unchanged = true;
        prev.forEach((id) => {
          if (!next.has(id)) {
            unchanged = false;
          }
        });
        if (unchanged) {
          return prev;
        }
      }
      return next;
    });
  }, [eventsWithDowntimeComments]);

  const handleExpandAllDowntimeComments = useCallback(() => {
    setCollapsedCommentEventIds(new Set());
  }, []);

  const handleCollapseAllDowntimeComments = useCallback((eventIds = []) => {
    setCollapsedCommentEventIds(new Set(eventIds));
  }, []);

  const toggleDowntimeCommentSection = useCallback((eventId) => {
    setCollapsedCommentEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  const departmentValidationFields = useMemo(
    () => [{ field: 'departmentId', label: 'Department', required: true }],
    [],
  );
  const departmentFields = useMemo(
    () => [
      {
        field: 'departmentId',
        label: 'Department',
        type: 'select',
        size: 12,
        required: true,
        options: departmentOptions,
      },
    ],
    [departmentOptions],
  );
  const {
    errors: departmentErrors,
    setErrors: setDepartmentErrors,
    validate: validateDepartment,
  } = useValidator(departmentValidationFields, departmentForm);
  const { renderField: renderDepartmentField } = useFormBuilder(
    departmentFields,
    departmentForm,
    setDepartmentForm,
    departmentErrors,
  );

  const prepareDepartmentSelection = useCallback(() => {
    setDepartmentForm({ departmentId: defaultDepartmentId ?? '' });
    setDepartmentErrors({});
  }, [defaultDepartmentId, setDepartmentErrors]);

  const handleReportStartDateUpdate = useCallback(
    (nextDate, nextTime) => {
      setShiftLogForm((prev) => {
        const reportStartDate = buildIsoFromParts(nextDate, nextTime);
        const next = {
          ...prev,
          reportStartDateDate: nextDate,
          reportStartDateTime: nextTime,
          reportStartDate,
        };
        if (reportStartDate) {
          next.shiftDate = toDateInputValue(reportStartDate);
        }
        if (reportStartDate && !reportEndDateTouched) {
          const nextEndDate = addMinutesToIso(reportStartDate, shiftDurationMinutes);
          const endParts = splitDateTimeParts(nextEndDate);
          next.reportEndDate = nextEndDate;
          next.reportEndDateDate = endParts.date;
          next.reportEndDateTime = endParts.time;
        }
        if (reportStartDate && !shiftNameTouched) {
          const shiftName = getShiftNameForTime(nextTime);
          if (shiftName) {
            next.shiftName = shiftName;
          }
        }
        return next;
      });
    },
    [getShiftNameForTime, reportEndDateTouched, shiftDurationMinutes, shiftNameTouched],
  );

  const handleReportEndDateUpdate = useCallback((nextDate, nextTime) => {
    setReportEndDateTouched(true);
    setShiftLogForm((prev) => ({
      ...prev,
      reportEndDateDate: nextDate,
      reportEndDateTime: nextTime,
      reportEndDate: buildIsoFromParts(nextDate, nextTime),
    }));
  }, []);

  const getDepartmentAndDescendants = useCallback(async (departmentId) => {
    const normalizedId = Number(departmentId);
    if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
      return [];
    }
    if (departmentChildrenCacheRef.current.has(normalizedId)) {
      return departmentChildrenCacheRef.current.get(normalizedId);
    }
    try {
      const response = await getDepartmentChildren(normalizedId);
      const ids = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.departmentIds)
            ? response.departmentIds
            : [];
      const normalizedIds = ids
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0);
      const finalIds = normalizedIds.length ? normalizedIds : [normalizedId];
      departmentChildrenCacheRef.current.set(normalizedId, finalIds);
      return finalIds;
    } catch (err) {
      console.error('Failed to load department descendants', err);
      const fallback = [normalizedId];
      departmentChildrenCacheRef.current.set(normalizedId, fallback);
      return fallback;
    }
  }, []);

  const shiftLogValidationFields = useMemo(
    () => [
      { field: 'userId', label: 'Supervisor Name', required: true },
      { field: 'departmentId', label: 'Department', required: true },
      { field: 'reportStartDateDate', label: 'Report Start Date', required: true },
      { field: 'reportStartDateTime', label: 'Report Start Time', required: true },
      { field: 'reportEndDateDate', label: 'Report End Date', required: true },
      { field: 'reportEndDateTime', label: 'Report End Time', required: true },
      { field: 'shiftName', label: 'Shift Name', required: true },
    ],
    [],
  );
  const shiftLogFields = useMemo(
    () => [
      {
        field: 'userId',
        label: 'Supervisor Name',
        type: 'select',
        size: 4,
        options: userOptions,
        required: true,
      },
      {
        field: 'departmentId',
        label: 'Department',
        type: 'select',
        size: 4,
        options: departmentOptions,
        required: true,
        disabled: true,
      },
      {
        field: 'shiftName',
        label: 'Shift Name',
        type: 'select',
        size: 4,
        options: SHIFT_NAME_OPTIONS,
        required: true,
        onChange: (opt, setFormData, field) => {
          setShiftNameTouched(true);
          setFormData((prev) => ({ ...prev, [field]: opt?.value ?? null }));
        },
      },
      {
        field: 'reportStartDateDate',
        label: 'Report Start Date',
        type: 'date',
        size: 3,
        required: true,
        onChange: (event) => {
          const nextDate = event.target.value;
          handleReportStartDateUpdate(nextDate, shiftLogForm?.reportStartDateTime || '');
        },
      },
      {
        field: 'reportStartDateTime',
        label: 'Report Start Time',
        type: 'time',
        size: 3,
        required: true,
        onChange: (event) => {
          const nextTime = event.target.value;
          handleReportStartDateUpdate(shiftLogForm?.reportStartDateDate || '', nextTime);
        },
      },
      {
        field: 'reportEndDateDate',
        label: 'Report End Date',
        type: 'date',
        size: 3,
        required: true,
        onChange: (event) => {
          const nextDate = event.target.value;
          handleReportEndDateUpdate(nextDate, shiftLogForm?.reportEndDateTime || '');
        },
      },
      {
        field: 'reportEndDateTime',
        label: 'Report End Time',
        type: 'time',
        size: 3,
        required: true,
        onChange: (event) => {
          const nextTime = event.target.value;
          handleReportEndDateUpdate(shiftLogForm?.reportEndDateDate || '', nextTime);
        },
      },
      {
        field: 'notes',
        label: primaryNotesLabel,
        type: 'textarea',
        size: 12,
        lines: 4,
      },
      {
        field: 'safteyNotes',
        label: secondaryNotesLabel,
        type: 'textarea',
        size: 12,
        lines: 4,
      },
      {
        field: 'files',
        label: 'Documents',
        type: 'files',
        size: 12,
        directory: 'shiftlogfiles',
      },
    ],
    [
      departmentOptions,
      handleReportEndDateUpdate,
      handleReportStartDateUpdate,
      shiftLogForm?.reportEndDateDate,
      shiftLogForm?.reportEndDateTime,
      shiftLogForm?.reportStartDateDate,
      shiftLogForm?.reportStartDateTime,
      userOptions,
    ],
  );
  const {
    errors: shiftLogErrors,
    setErrors: setShiftLogErrors,
    validate: validateShiftLog,
  } = useValidator(shiftLogValidationFields, shiftLogForm);
  const { renderField: renderShiftLogFieldBase } = useFormBuilder(
    shiftLogFields,
    shiftLogForm,
    setShiftLogForm,
    shiftLogErrors,
  );

  const buildShiftLogSnapshot = useCallback(
    (formState, downtimeState = downTimeEvents, upcomingState = upcomingEvents) => {
      const crew = Array.isArray(formState?.crew)
        ? formState.crew
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
          .sort((a, b) => a - b)
        : [];
      const contractorList = Array.isArray(formState?.contractorList)
        ? [...formState.contractorList].map((name) => String(name || '')).sort((a, b) => a.localeCompare(b))
        : [];
      const normalized = {
        userId: formState?.userId ?? '',
        departmentId: formState?.departmentId ?? '',
        shiftDate: normalizeDateForPayload(formState?.shiftDate),
        shiftName: formState?.shiftName ?? '',
        crew,
        contractorList,
        notes: formState?.notes ?? '',
        safteyNotes: formState?.safteyNotes ?? '',
        completed: Number(formState?.completed) === 1 ? 1 : 0,
        reportStartDate: formState?.reportStartDate ?? null,
        reportEndDate: formState?.reportEndDate ?? null,
        files: serializeFilesForPayload(formState?.files || []),
        downtimeList: Array.isArray(formState?.downtimeList)
          ? formState.downtimeList
          : Array.isArray(downtimeState)
            ? downtimeState
            : [],
        upcomingEvents: Array.isArray(formState?.upcomingEvents)
          ? formState.upcomingEvents
          : Array.isArray(upcomingState)
            ? upcomingState
            : [],
        downTimeEvents: Array.isArray(downtimeState) ? downtimeState : [],
        upcomingEventsState: Array.isArray(upcomingState) ? upcomingState : [],
      };

      return JSON.stringify(normalized);
    },
    [downTimeEvents, upcomingEvents],
  );

  const updateSavedSnapshot = useCallback(
    (formState = shiftLogForm, downtimeState = downTimeEvents, upcomingState = upcomingEvents) => {
      savedSnapshotRef.current = buildShiftLogSnapshot(formState, downtimeState, upcomingState);
    },
    [buildShiftLogSnapshot, downTimeEvents, shiftLogForm, upcomingEvents],
  );

  useEffect(() => {
    if (!showFormModal || isReadOnly) {
      setHasPendingChanges(false);
      return;
    }

    const snapshot = buildShiftLogSnapshot(shiftLogForm, downTimeEvents, upcomingEvents);
    const savedSnapshot = savedSnapshotRef.current;

    if (!savedSnapshot) {
      setHasPendingChanges(true);
      return;
    }

    setHasPendingChanges(snapshot !== savedSnapshot);
  }, [buildShiftLogSnapshot, downTimeEvents, isReadOnly, shiftLogForm, showFormModal, upcomingEvents]);

  const notesValue = shiftLogForm?.notes ?? '';
  const notesError = shiftLogErrors?.notes;

  const handleNotesChange = useCallback(
    (event) => {
      const { value } = event.target;
      setShiftLogForm((prev) => ({ ...prev, notes: value }));
    },
    [setShiftLogForm],
  );

  const handleGenerateAiSummary = useCallback(async () => {
    if (isReadOnly) {
      return;
    }

    setAiSummaryLoading(true);
    try {
      const response = await getDailyAISummary();
      const summaryText = typeof response?.summary === 'string' ? response.summary.trim() : '';
      if (!summaryText) {
        return;
      }

      setShiftLogForm((prev) => {
        const previousNotes = prev?.notes ?? '';
        const updatedNotes = previousNotes ? `${previousNotes}\n\n${summaryText}` : summaryText;
        return { ...prev, notes: updatedNotes };
      });
    } catch (error) {
      console.error('Failed to generate AI summary for shift notes:', error);
    } finally {
      setAiSummaryLoading(false);
    }
  }, [isReadOnly, setShiftLogForm]);

  const renderNotesField = useCallback(
    (field) => {
      const size = field.size ?? 12;
      const hidden = field.hideLabel ? 'd-none' : '';
      const isFieldDisabled = isReadOnly ? true : field.disabled;
      const isFieldReadOnly = isReadOnly || field.readOnly;

      return (
        <div className={`col-md-${size}`} key={field.field}>
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap my-8">
            <label className={`form-label mb-8 ${hidden}`}>
              {field.label}
              {field.required && <span className="text-danger mx-4">*</span>}
            </label>
            {!isReadOnly && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                onClick={handleGenerateAiSummary}
                disabled={aiSummaryLoading}
              >
                {aiSummaryLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <Icon icon="mage:stars-c" className="text-xl" />
                    AI Summary (Beta)
                  </>
                )}
              </button>
            )}
          </div>
          <textarea
            name={field.field}
            className={`form-control ${notesError ? 'is-invalid' : ''}`}
            value={notesValue}
            rows={field.lines || 3}
            onChange={handleNotesChange}
            disabled={isFieldDisabled}
            readOnly={isFieldReadOnly}
          />
          {notesError && <div className="invalid-feedback">{notesError}</div>}
        </div>
      );
    },
    [aiSummaryLoading, handleGenerateAiSummary, handleNotesChange, isReadOnly, notesError, notesValue],
  );

  const renderShiftLogField = useCallback(
    (field) => {
      if (field.field === 'notes') {
        return renderNotesField(field);
      }

      return renderShiftLogFieldBase({
        ...field,
        disabled: isReadOnly ? true : field.disabled,
        readOnly: isReadOnly || field.readOnly,
      });
    },
    [isReadOnly, renderNotesField, renderShiftLogFieldBase],
  );

  const enhanceCrewOptions = useCallback((options) => {
    const base = Array.isArray(options) ? options.filter(Boolean) : [];
    const withoutAdd = base.filter(
      (opt) => opt && opt.value !== ADD_CREW_MEMBER_OPTION.value,
    );
    withoutAdd.sort((a, b) => {
      const labelA = a?.label || '';
      const labelB = b?.label || '';
      return labelA.localeCompare(labelB);
    });
    return [...withoutAdd, ADD_CREW_MEMBER_OPTION];
  }, []);

  const crewDisplay = useMemo(() => {
    const map = new Map((crewOptions || []).map((opt) => [opt.value, opt.label]));
    const crew = Array.isArray(shiftLogForm?.crew) ? shiftLogForm.crew : [];
    return crew.map((id) => ({ id, label: map.get(id) || `Technician ${id}` }));
  }, [crewOptions, shiftLogForm?.crew]);

  const eventValidationFields = useMemo(() => {
    const status = Number(eventForm?.eventStatus);
    const eventType = Number(eventForm?.eventTypeId);
    const isBreakdownEvent = eventType === 2;
    const isInProgress = status === 1;
    const isComplete = status === 2;
    const isCancelled = status === 3;
    const isPending = status === 4;

    const plannedStartRequired = !(
      (isInProgress && !isBreakdownEvent) ||
      (isComplete && !isBreakdownEvent) ||
      isCancelled ||
      isPending
    );

    const plannedEndRequired = !(
      (isComplete && !isBreakdownEvent) || isCancelled || isPending
    );

    const startRequired = (isInProgress || isComplete) && !isCancelled;
    const endRequired = isComplete && !isCancelled;

    return [
      { field: 'name', label: 'Name', required: true },
      { field: 'eventTypeId', label: 'Event Type', required: true },
      { field: 'assetId', label: 'Asset', required: true },
      { field: 'eventLocationId', label: 'Event Location' },
      { field: 'eventStatus', label: 'Status', required: true },
      {
        field: 'plannedStartDate',
        label: 'Planned Start Date',
        required: plannedStartRequired,
        type: 'datetime',
      },
      {
        field: 'plannedEndDate',
        label: 'Planned End Date',
        required: plannedEndRequired,
        type: 'datetime',
      },
      { field: 'startDate', label: 'Start Date', required: startRequired, type: 'datetime' },
      { field: 'endDate', label: 'End Date', required: endRequired, type: 'datetime' },
    ];
  }, [eventForm?.eventStatus, eventForm?.eventTypeId]);

  const {
    errors: eventFormErrors,
    setErrors: setEventFormErrors,
    validate: validateEventForm,
  } = useValidator(eventValidationFields, eventForm);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const [statuses, types, woStatuses, responsibilities] = await Promise.all([
          getEventStatus().catch((error) => {
            console.error('Failed to load event statuses', error);
            return [];
          }),
          getEventTypes().catch((error) => {
            console.error('Failed to load event types', error);
            return [];
          }),
          getWorkOrderStatus().catch((error) => {
            console.error('Failed to load work order statuses', error);
            return [];
          }),
          getEventResponsibilities().catch((error) => {
            console.error('Failed to load event responsibilities', error);
            return [];
          }),
        ]);
        if (!isMounted) return;
        setEventStatusOptions(
          (statuses || []).map((status) => ({
            value: status.id,
            label: status.name,
            colours: status.colours || {},
          })),
        );
        setEventTypeOptions(
          (types || []).map((type) => ({
            value: type.id,
            label: type.name,
          })),
        );
        setEventWorkOrderStatuses(
          (woStatuses || []).map((status) => ({
            value: status.id,
            label: status.name,
            bootstrapName: status.colours?.bootstrapName || 'secondary',
          })),
        );
        const responsibilityOptions = (responsibilities || []).map((responsibility) => ({
          value: responsibility.id,
          label: responsibility.name,
          isDefault: responsibility.default === 1 || responsibility.default === true,
        }));
        setEventResponsibilityOptions(responsibilityOptions);
        const defaultOption = responsibilityOptions.find((option) => option.isDefault);
        setDefaultEventResponsibilityId(defaultOption?.value ?? null);
      } catch (error) {
        console.error('Failed to load event metadata', error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (delayForm.delayEndDate && delayForm.delayStartDate) {
      const duration =
        (new Date(delayForm.delayEndDate) - new Date(delayForm.delayStartDate)) /
        (1000 * 60 * 60);
      setDelayDuration(Number.isFinite(duration) ? parseFloat(duration.toFixed(2)) : 0);
    } else {
      setDelayDuration(0);
    }
  }, [delayForm.delayStartDate, delayForm.delayEndDate]);

  useEffect(() => {
    let isMounted = true;
    getEventDelayCodes()
      .then((data) => {
        if (isMounted) {
          setDelayCodes(data || []);
        }
      })
      .catch((error) => {
        console.error('Failed to load delay codes', error);
        if (isMounted) {
          setDelayCodes([]);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const mapAssetsToOptions = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((asset) => {
        if (!asset) return null;
        const id = asset.id ?? asset.assetId;
        if (id === undefined || id === null) return null;
        const number = asset.assetNumber || asset.unitNumber || '';
        const description = asset.assetDescription || '';
        const label = [number, description].filter(Boolean).join(' - ') || `Asset ${id}`;
        return { value: id, label };
      })
      .filter(Boolean);
  }, []);

  const loadEventAssets = useCallback(
    async (selectedOption = null) => {
      try {
        const response = await getPaginatedAssets({ page: 1, pageSize: 25 });
        const rows = response?.data || [];
        const options = mapAssetsToOptions(rows);
        if (
          selectedOption &&
          !options.some((opt) => String(opt.value) === String(selectedOption.value))
        ) {
          options.unshift(selectedOption);
        }
        setEventAssetOptions(options);
        return options;
      } catch (error) {
        console.error('Failed to load assets', error);
        if (selectedOption) {
          setEventAssetOptions([selectedOption]);
          return [selectedOption];
        }
        setEventAssetOptions([]);
        return [];
      }
    },
    [mapAssetsToOptions],
  );

  const handleEventAssetSearch = useCallback(
    async (inputValue) => {
      try {
        const response = await getPaginatedAssets({
          page: 1,
          pageSize: 25,
          search: inputValue,
        });
        const rows = response?.data || [];
        const options = mapAssetsToOptions(rows);
        setEventAssetOptions(options);
        return options;
      } catch (error) {
        console.error('Failed to search assets', error);
        return [];
      }
    },
    [mapAssetsToOptions],
  );

  const loadEventDelays = useCallback(async (eventId) => {
    if (!eventId) {
      setEventDelays([]);
      return;
    }
    try {
      const response = await getEventDelayByEventId(eventId);
      const delays = response?.data || response || [];
      setEventDelays(Array.isArray(delays) ? delays : []);
    } catch (error) {
      console.error('Failed to load event delays', error);
      setEventDelays([]);
    }
  }, []);

  const loadEventWorkOrders = useCallback(async (eventData) => {
    if (!eventData || !eventData.assetId) {
      setEventAssignedWorkOrders([]);
      setEventAvailableWorkOrders([]);
      return;
    }
    try {
      const response = await getPaginatedWorkOrders({
        page: 1,
        pageSize: 1000,
        assetId: [eventData.assetId],
      });
      const rows = response?.data || response || [];
      const fetchedAssigned = rows.filter((wo) => String(wo.eventId) === String(eventData.id) && wo.eventId !== null);
      const resolvedAssigned = eventData?.id ? fetchedAssigned : eventAssignedWorkOrders;
      const existingAssignedIds = new Set((resolvedAssigned || []).map((wo) => wo.id));
      const available = rows.filter((wo) => wo.eventId === null && !existingAssignedIds.has(wo.id));
      if (eventData?.id || !eventAssignedWorkOrders.length) {
        setEventAssignedWorkOrders(fetchedAssigned);
      }
      setEventAvailableWorkOrders(available);
    } catch (error) {
      console.error('Failed to load event work orders', error);
      setEventAssignedWorkOrders([]);
      setEventAvailableWorkOrders([]);
    }
  }, [eventAssignedWorkOrders]);

  useEffect(() => {
    if (!eventModalOpen || editingEvent) {
      return;
    }

    if (!eventForm?.assetId) {
      setEventAvailableWorkOrders([]);
      setEventAssignedWorkOrders([]);
      return;
    }

    loadEventWorkOrders({ assetId: eventForm.assetId, id: null });
  }, [editingEvent, eventForm?.assetId, eventModalOpen]);

  const handleDelaySave = useCallback(async () => {
    const isBaseValid = validateDelay();
    const customErrors = validateDelayDateRange(
      delayForm.delayStartDate,
      delayForm.delayEndDate,
      eventForm,
      editingEvent,
    );
    if (Object.keys(customErrors).length) {
      setDelayErrors((prev) => ({ ...prev, ...customErrors }));
    }
    if (!isBaseValid || Object.keys(customErrors).length) return;

    const eventStart = eventForm.startDate
      ? new Date(eventForm.startDate)
      : eventForm.plannedStartDate
        ? new Date(eventForm.plannedStartDate)
        : null;
    const eventEnd = eventForm.endDate
      ? new Date(eventForm.endDate)
      : eventForm.plannedEndDate
        ? new Date(eventForm.plannedEndDate)
        : null;

    const durationHours = eventEnd && eventStart ? Math.abs(eventEnd - eventStart) / (1000 * 60 * 60) : null;
    const existingTotal = eventDelays.reduce((sum, d) => sum + calcDelayDuration(d), 0);
    const updatedTotal = editingDelay
      ? existingTotal - calcDelayDuration(editingDelay) + delayDuration
      : existingTotal + delayDuration;
    if (Number.isFinite(durationHours) && updatedTotal >= durationHours) {
      alert('Total delay duration must be less than the event duration');
      return;
    }

    const delayStart = delayForm.delayStartDate ? new Date(delayForm.delayStartDate) : null;
    const delayEnd = delayForm.delayEndDate ? new Date(delayForm.delayEndDate) : null;

    const payload = {
      eventDelayCodeId: Number(delayForm.eventDelayCodeId),
      delayStartDate: delayStart ? delayStart.toISOString() : null,
      delayEndDate: delayEnd ? delayEnd.toISOString() : null,
    };
    try {
      if (editingEvent?.id) {
        const remotePayload = { ...payload, eventsId: editingEvent.id };
        if (editingDelay) {
          await updateEventDelay(editingDelay.id, remotePayload);
        } else {
          await createEventDelay(remotePayload);
        }
        await loadEventDelays(editingEvent.id);
        setShowDelayModal(false);
      } else {
        const delayCode = delayCodes.find(
          (code) => String(code.id) === String(payload.eventDelayCodeId),
        );
        setEventDelays((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const updatedDelay = {
            ...editingDelay,
            id: editingDelay?.id ?? Date.now(),
            ...payload,
            eventDelayCode: delayCode || editingDelay?.eventDelayCode || null,
          };
          const next = editingDelay
            ? existing.map((entry) => (entry.id === editingDelay.id ? updatedDelay : entry))
            : [updatedDelay, ...existing];
          return next;
        });
        setShowDelayModal(false);
      }
    } catch (err) {
      console.error('Error saving delay', err);
    }
  }, [
    delayDuration,
    delayForm,
    editingDelay,
    delayCodes,
    editingEvent,
    eventDelays,
    eventForm,
    loadEventDelays,
    setDelayErrors,
    validateDelay,
  ]);

  const handleEditDelay = useCallback((delay) => {
    setEditingDelay(delay);
    setDelayForm({
      eventDelayCodeId: delay.eventDelayCodeId,
      delayStartDate: toLocalInputFormat(delay.delayStartDate),
      delayEndDate: toLocalInputFormat(delay.delayEndDate),
    });
    setDelayErrors({});
    setShowDelayModal(true);
  }, []);

  const handleAddDelay = useCallback(() => {
    const defaultStartDate = computeDefaultDelayStartDate(eventForm, editingEvent);
    setEditingDelay(null);
    setDelayForm({
      eventDelayCodeId: '',
      delayStartDate: defaultStartDate ? toLocalInputFormat(defaultStartDate.toISOString()) : '',
      delayEndDate: '',
    });
    setDelayErrors({});
    setShowDelayModal(true);
  }, [editingEvent, eventForm]);

  const handleDeleteDelay = useCallback(
    async (id) => {
      if (!window.confirm('Delete delay?')) return;
      try {
        if (editingEvent?.id) {
          await deleteEventDelay(id);
          await loadEventDelays(editingEvent.id);
        } else {
          setEventDelays((prev) => (Array.isArray(prev) ? prev.filter((d) => d.id !== id) : []));
        }
      } catch (err) {
        console.error('Error deleting delay', err);
      }
    },
    [editingEvent?.id, loadEventDelays],
  );

  const firstAssignedWorkOrder = useMemo(() => {
    if (!Array.isArray(eventAssignedWorkOrders) || eventAssignedWorkOrders.length === 0) {
      return null;
    }
    if (editingEvent?.id) {
      const match = eventAssignedWorkOrders.find(
        (wo) => String(wo.eventId) === String(editingEvent.id),
      );
      if (match) return match;
    }
    return eventAssignedWorkOrders[0];
  }, [eventAssignedWorkOrders, editingEvent?.id]);

  const normalizeCrewArray = useCallback((value) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry) => {
        if (entry === null || entry === undefined) return null;
        if (typeof entry === 'object') {
          if (entry.userId !== undefined && entry.userId !== null) {
            return entry.userId;
          }
          if (entry.id !== undefined && entry.id !== null) {
            return entry.id;
          }
        }
        return entry;
      })
      .filter((id) => id !== null && id !== undefined);
  }, []);

  const crewsMatch = useCallback(
    (a, b) => {
      const normA = [...new Set(normalizeCrewArray(a))].sort();
      const normB = [...new Set(normalizeCrewArray(b))].sort();
      if (normA.length !== normB.length) return false;
      return normA.every((val, index) => val === normB[index]);
    },
    [normalizeCrewArray],
  );

  const handleWorkOrderStatusChange = useCallback(
    async (workOrderId, statusId) => {
      try {
        await updateWorkOrder(workOrderId, { statusId });
        const statusObj = eventWorkOrderStatuses.find(
          (status) => String(status.value) === String(statusId),
        );
        const status = statusObj
          ? {
            id: statusObj.value,
            name: statusObj.label,
            colours: { bootstrapName: statusObj.bootstrapName },
          }
          : null;
        setEventAssignedWorkOrders((prev) =>
          prev.map((wo) => (wo.id === workOrderId ? { ...wo, statusId: Number(statusId), status } : wo)),
        );
      } catch (error) {
        console.error('Failed to update work order status', error);
      }
    },
    [eventWorkOrderStatuses],
  );

  const handleCompleteAllWork = useCallback(async () => {
    const completeStatus = eventWorkOrderStatuses.find(
      (status) => status.label && status.label.toLowerCase() === 'complete',
    );
    if (!completeStatus) return;
    const { eventStart, eventEnd } = getEventRange(eventForm);
    const hasOutsideRange = hasWorkOrdersOutsideEventRange(eventAssignedWorkOrders, eventStart, eventEnd);
    if (hasOutsideRange) {
      const shouldAlignDates = window.confirm(
        'One or more Work Order start and end dates falls outside the event start / end date. Do you want to align these work orders to the event start and end date / time?',
      );
      if (!shouldAlignDates) {
        return;
      }
    }
    try {
      await Promise.all(
        eventAssignedWorkOrders.map((wo) =>
          updateWorkOrder(
            wo.id,
            buildCompleteWorkOrderPayload(
              wo,
              completeStatus.value,
              eventStart,
              eventEnd,
              hasOutsideRange,
            ),
          ),
        ),
      );
      setEventAssignedWorkOrders((prev) =>
        prev.map((wo) => ({
          ...wo,
          ...buildCompleteWorkOrderPayload(
            wo,
            completeStatus.value,
            eventStart,
            eventEnd,
            hasOutsideRange,
          ),
          statusId: completeStatus.value,
          status: {
            id: completeStatus.value,
            name: completeStatus.label,
            colours: { bootstrapName: completeStatus.bootstrapName },
          },
        })),
      );
    } catch (error) {
      console.error('Failed to complete work orders', error);
    }
  }, [eventAssignedWorkOrders, eventForm, eventWorkOrderStatuses]);

  const handleEventSelectAllAssign = useCallback(() => {
    setEventAssignSelected(eventAssignOptions.map((wo) => wo.id));
  }, [eventAssignOptions]);

  const handleEventDeselectAllAssign = useCallback(() => {
    setEventAssignSelected([]);
  }, []);

  const openEventAssignModal = useCallback(() => {
    const eligible = eventAvailableWorkOrders.filter(
      (wo) =>
        !wo.eventId &&
        ['open', 'in progress'].includes((wo.status?.name || '').toLowerCase()),
    );
    setEventAssignOptions(eligible);
    setEventAssignSelected([]);
    setEventAssignSearch('');
    setShowEventAssignModal(true);
  }, [eventAvailableWorkOrders]);

  const handleEventAssignSelected = useCallback(async () => {
    if (!eventAssignSelected.length) {
      setShowEventAssignModal(false);
      return;
    }
    const eventId = editingEvent?.id;
    const newlySelected = eventAssignOptions.filter((wo) => eventAssignSelected.includes(wo.id));
    try {
      if (eventId) {
        await Promise.all(
          eventAssignSelected.map((id) => updateWorkOrder(id, { eventId })),
        );
      }
      setEventAssignedWorkOrders((prev) => {
        const existingIds = new Set(prev.map((wo) => wo.id));
        const additions = newlySelected.filter((wo) => !existingIds.has(wo.id));
        const mappedAdditions = additions.map((wo) => ({
          ...wo,
          eventId: eventId ?? wo.eventId ?? null,
        }));
        return [...prev, ...mappedAdditions];
      });
      setEventAvailableWorkOrders((prev) =>
        prev.filter((wo) => !eventAssignSelected.includes(wo.id)),
      );
      setEventAssignOptions([]);
      setEventAssignSelected([]);
      setShowEventAssignModal(false);
    } catch (error) {
      console.error('Failed to assign work orders to event', error);
    }
  }, [editingEvent?.id, eventAssignOptions, eventAssignSelected, updateWorkOrder]);

  const handleDeleteEventWorkOrder = useCallback(
    async (id) => {
      if (!window.confirm('Remove work order from event?')) return;
      const eventId = editingEvent?.id;
      try {
        if (eventId) {
          await updateWorkOrder(id, { eventId: null });
        }
        const removed = eventAssignedWorkOrders.find((wo) => wo.id === id);
        setEventAssignedWorkOrders((prev) => prev.filter((wo) => wo.id !== id));
        if (removed) {
          setEventAvailableWorkOrders((prev) => [...prev, { ...removed, eventId: null }]);
        }
      } catch (error) {
        console.error('Failed to remove work order from event', error);
      }
    },
    [editingEvent?.id, eventAssignedWorkOrders, updateWorkOrder],
  );

  const handleCloseEventModal = useCallback(() => {
    setEventModalOpen(false);
    setEditingEvent(null);
    setEventForm({});
    setEventFormErrors({});
    setEventAssignedWorkOrders([]);
    setEventAvailableWorkOrders([]);
    setShowEventAssignModal(false);
    setEventAssignOptions([]);
    setEventAssignSelected([]);
    setEventAssignSearch('');
    setEventDelays([]);
    setShowDelayModal(false);
    setEditingDelay(null);
    setDelayForm({
      eventDelayCodeId: '',
      delayStartDate: '',
      delayEndDate: '',
    });
    setDelayErrors({});
  }, [setEventFormErrors, setDelayErrors]);

  const handleAddEventClick = useCallback(async () => {
    if (isReadOnly) return;
    setEventFormErrors({});
    setEditingEvent(null);
    setEventDelays([]);
    setEditingDelay(null);
    setShowDelayModal(false);
    setDelayForm({
      eventDelayCodeId: '',
      delayStartDate: '',
      delayEndDate: '',
    });
    setDelayErrors({});

    const defaultName = selectedWorkOrder
      ? [selectedWorkOrder.workOrderNumber, selectedWorkOrder.workOrderTitle]
        .filter(Boolean)
        .join(' – ')
      : '';
    const assetId = selectedWorkOrder?.assetId || '';
    const selectedAssetOption = assetId
      ? {
        value: assetId,
        label:
          [
            selectedWorkOrder?.asset?.assetNumber || selectedWorkOrder?.asset?.unitNumber,
            selectedWorkOrder?.asset?.assetDescription,
          ]
            .filter(Boolean)
            .join(' - ') || `Asset ${assetId}`,
      }
      : null;

    setEventForm({
      name: defaultName,
      eventTypeId: '',
      eventStatus: pendingEventStatusId ?? eventStatusOptions?.[0]?.value ?? '',
      assetId,
      plannedStartDate: '',
      plannedEndDate: '',
      startDate: '',
      endDate: '',
      systemId: null,
      eventLocationId: null,
      eventResponsibilityId: defaultEventResponsibilityId ?? null,
      crew: Array.isArray(selectedWorkOrder?.crew) ? selectedWorkOrder.crew : [],
    });

    const assignedOrders = selectedWorkOrder ? [selectedWorkOrder] : [];
    setEventAssignedWorkOrders(assignedOrders);
    setEventAvailableWorkOrders([]);
    setShowEventAssignModal(false);
    setEventAssignOptions([]);
    setEventAssignSelected([]);
    setEventAssignSearch('');
    setEventComments([]);
    setEventDelays([]);
    await loadEventAssets(selectedAssetOption);
    setEventModalOpen(true);
  }, [
    defaultEventResponsibilityId,
    eventStatusOptions,
    isReadOnly,
    loadEventAssets,
    pendingEventStatusId,
    selectedWorkOrder,
    setDelayErrors,
    setEventFormErrors,
  ]);

  const handleEditEventClick = useCallback(
    async (eventItem) => {
      if (isReadOnly) return;
      if (!eventItem || !eventItem.id) return;
      setEventFormErrors({});
      setShowEventAssignModal(false);
      setEventAssignOptions([]);
      setEventAssignSelected([]);
      setEventAssignSearch('');
      setEventComments([]);
      try {
        let detailed = null;
        try {
          detailed = await getEventsById(eventItem.id);
        } catch (error) {
          console.error('Failed to load detailed event information', error);
        }
        const eventData = detailed && detailed.id ? detailed : eventItem;
        setEditingEvent(eventData);
        setEventForm({
          name: eventData.name || '',
          eventTypeId: eventData.eventTypeId || eventData.eventType?.id || '',
          eventStatus:
            eventData.eventStatus !== undefined && eventData.eventStatus !== null
              ? eventData.eventStatus
              : eventData.eventStatusId || '',
          assetId: eventData.assetId || '',
          plannedStartDate: toLocalInputFormat(eventData.plannedStartDate),
          plannedEndDate: toLocalInputFormat(eventData.plannedEndDate),
          startDate: toLocalInputFormat(eventData.startDate),
          endDate: toLocalInputFormat(eventData.endDate),
          systemId: eventData.systemId ?? null,
          eventLocationId:
            eventData.eventLocationId ?? eventData.eventLocation?.id ?? null,
          eventResponsibilityId:
            eventData.eventResponsibilityId ?? eventData.eventResponsibility?.id ?? null,
          crew: Array.isArray(eventData.crew) ? eventData.crew : [],
        });
        const selectedAssetOption = eventData.assetId
          ? {
            value: eventData.assetId,
            label:
              [
                eventData.asset?.assetNumber || eventData.asset?.unitNumber,
                eventData.asset?.assetDescription,
              ]
                .filter(Boolean)
                .join(' - ') || `Asset ${eventData.assetId}`,
          }
          : null;
        await loadEventAssets(selectedAssetOption);
        await Promise.all([
          loadEventWorkOrders({ ...eventData, assetId: eventData.assetId || selectedAssetOption?.value }),
          loadEventDelays(eventData.id),
        ]);
        setEventModalOpen(true);
      } catch (error) {
        console.error('Failed to open event editor', error);
      }
    },
    [
      isReadOnly,
      loadEventAssets,
      loadEventDelays,
      loadEventWorkOrders,
      setEventFormErrors,
    ],
  );

  useEffect(() => {
    if (!eventModalOpen || editingEvent || !defaultEventResponsibilityId) {
      return;
    }
    setEventForm((prev) => {
      if (prev?.eventResponsibilityId) return prev;
      return {
        ...prev,
        eventResponsibilityId: defaultEventResponsibilityId,
      };
    });
  }, [defaultEventResponsibilityId, editingEvent, eventModalOpen]);

  const loadShiftContextData = useCallback(async (departmentId, reportStartDate, reportEndDate) => {
    if (!departmentId) {
      setDownTimeEvents([]);
      setInProgressEvents([]);
      setRecentlyCompletedEvents([]);
      setUpcomingEvents([]);
      return;
    }
    try {
      const pageSize = 1000;
      const resolvedStart = reportStartDate ? new Date(reportStartDate) : null;
      const resolvedEnd = reportEndDate ? new Date(reportEndDate) : null;
      const fallbackStart = new Date();
      const fallbackEnd = new Date(fallbackStart.getTime() + shiftDurationMinutes * 60 * 1000);
      const rangeStart = resolvedStart && !Number.isNaN(resolvedStart.getTime()) ? resolvedStart : fallbackStart;
      const rangeEnd = resolvedEnd && !Number.isNaN(resolvedEnd.getTime()) ? resolvedEnd : fallbackEnd;
      const upcomingEnd = new Date(rangeEnd.getTime() + 24 * 60 * 60 * 1000);
      const departmentIds = await getDepartmentAndDescendants(departmentId);
      const resolvedDepartmentId = Number(departmentId);
      const departmentFilter = departmentIds.length
        ? departmentIds
        : Number.isInteger(resolvedDepartmentId) && resolvedDepartmentId > 0
          ? [resolvedDepartmentId]
          : [];
      if (!departmentFilter.length) {
        setDownTimeEvents([]);
        setInProgressEvents([]);
        setRecentlyCompletedEvents([]);
        setUpcomingEvents([]);
        return;
      }
      const recentWindowMs = 24 * 60 * 60 * 1000;
      const recentWindowStart = new Date(rangeStart.getTime() - recentWindowMs);
      const recentWindowEnd = new Date();
      const [inProgressResponse, completedResponse, upcomingResponse] = await Promise.all([
        getPaginatedEvents({ // All In progress events
          page: 1,
          pageSize,
          eventStatusId: [1],
          departmentId: departmentFilter,
        }),
        getPaginatedEvents({ // recently completed events
          page: 1,
          pageSize,
          eventStatusId: [2],
          departmentId: departmentFilter,
          endDateStart: recentWindowStart.toISOString(),
          endDate: recentWindowEnd.toISOString(),
        }),
        getPaginatedEvents({ // All Planned Events
          page: 1,
          pageSize,
          eventStatusId: [4],
          departmentId: departmentFilter,
          // plannedStartDate: rangeEnd.toISOString(),
          // plannedEndDate: upcomingEnd.toISOString(),
        }),
      ]);
      const inProgressData = Array.isArray(inProgressResponse?.data) ? inProgressResponse.data : [];
      const completedData = Array.isArray(completedResponse?.data) ? completedResponse.data : [];
      const recentCompleted = completedData.filter((event) => {
        const completedAt = event?.endDate || event?.completedAt;
        if (!completedAt) {
          return false;
        }
        const completedDate = new Date(completedAt);
        if (Number.isNaN(completedDate.getTime())) {
          return false;
        }
        return (
          completedDate.getTime() >= recentWindowStart.getTime()
          && completedDate.getTime() <= recentWindowEnd.getTime()
        );
      });
      setInProgressEvents(inProgressData);
      setRecentlyCompletedEvents(recentCompleted);
      setDownTimeEvents([...inProgressData, ...recentCompleted]);
      setUpcomingEvents(upcomingResponse?.data || []);
    } catch (err) {
      console.error('Failed to load shift log context data', err);
      setDownTimeEvents([]);
      setInProgressEvents([]);
      setRecentlyCompletedEvents([]);
      setUpcomingEvents([]);
    }
  }, [getDepartmentAndDescendants, shiftDurationMinutes]);

  useEffect(() => {
    const eventIds = Array.isArray(downTimeEvents)
      ? downTimeEvents
        .map((event) => Number(event?.id ?? event?.eventId))
        .filter((id) => Number.isFinite(id) && id > 0)
      : [];

    if (!eventIds.length) {
      setDowntimeComments([]);
      setDowntimeCommentsError(null);
      setDowntimeCommentsLoading(false);
      return;
    }

    let cancelled = false;
    setDowntimeCommentsLoading(true);
    setDowntimeCommentsError(null);

    (async () => {
      try {
        const response = await getDowntimeComments(eventIds);
        if (cancelled) return;
        setDowntimeComments(Array.isArray(response) ? response : []);
        setDowntimeCommentsError(null);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load event comments', error);
        setDowntimeComments([]);
        setDowntimeCommentsError('Failed to load event comments.');
      } finally {
        if (!cancelled) {
          setDowntimeCommentsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [downTimeEvents]);

  const handleEventModalSave = useCallback(async () => {
    if (!validateEventForm()) return;
    const eventStatus = Number(eventForm?.eventStatus);
    const { eventStart, eventEnd } = getEventRange(eventForm);
    if (eventStatus === 2 && completeEventWorkOrdersSetting === true) {
      const canComplete = areWorkOrdersCompletableForEvent(
        eventAssignedWorkOrders,
        eventStart,
        eventEnd,
      );
      if (!canComplete) {
        window.alert(
          'All event Work Orders must have status Complete, Cancelled or otherwise removed from the event to complete the event.',
        );
        return;
      }
    }
    const workOrderIds = Array.from(
      new Set(
        eventAssignedWorkOrders
          .map((wo) => Number(wo?.id))
          .filter((id) => Number.isFinite(id)),
      ),
    );
    const payload = {
      name: eventForm.name,
      assetId: eventForm.assetId ? Number(eventForm.assetId) : null,
      eventTypeId: eventForm.eventTypeId ? Number(eventForm.eventTypeId) : null,
      plannedStartDate: eventForm.plannedStartDate
        ? new Date(eventForm.plannedStartDate).toISOString()
        : null,
      plannedEndDate: eventForm.plannedEndDate
        ? new Date(eventForm.plannedEndDate).toISOString()
        : null,
      startDate: eventForm.startDate ? new Date(eventForm.startDate).toISOString() : null,
      endDate: eventForm.endDate ? new Date(eventForm.endDate).toISOString() : null,
      eventStatus: eventForm.eventStatus !== undefined && eventForm.eventStatus !== null
        ? Number(eventForm.eventStatus)
        : null,
      systemId:
        eventForm.systemId !== undefined && eventForm.systemId !== null && eventForm.systemId !== ''
          ? Number(eventForm.systemId)
          : null,
      eventLocationId:
        eventForm.eventLocationId !== undefined &&
          eventForm.eventLocationId !== null &&
          eventForm.eventLocationId !== ''
          ? Number(eventForm.eventLocationId)
          : null,
      eventResponsibilityId:
        eventForm.eventResponsibilityId !== undefined &&
          eventForm.eventResponsibilityId !== null &&
          eventForm.eventResponsibilityId !== ''
          ? Number(eventForm.eventResponsibilityId)
          : null,
      workOrderIds,
    };
    const pendingComments = !editingEvent && Array.isArray(eventComments) ? eventComments : [];
    const pendingDelays = !editingEvent && Array.isArray(eventDelays) ? eventDelays : [];
    const baseEvent = editingEvent || {};
    const nextEvent = {
      ...baseEvent,
      ...payload,
      assetId: payload.assetId ?? baseEvent.assetId,
    };
    try {
      let resolvedEvent = null;
      if (editingEvent?.id) {
        const updated = await updateEvents(editingEvent.id, payload);
        resolvedEvent = updated && updated.id ? updated : nextEvent;
      } else {
        const created = await createEvents(payload);
        resolvedEvent = created && created.id ? created : { ...nextEvent, id: created?.id ?? null };
        const createdId = resolvedEvent?.id;
        if (createdId) {
          if (pendingComments.length) {
            const commentPayloads = pendingComments
              .map((comment) => {
                const comments = (comment?.comments || comment?.comment || '').trim();
                if (!comments) return null;
                const userId =
                  comment?.userId ?? comment?.user?.id ?? comment?.user?.userId ?? userProfile?.id ?? null;
                if (!userId) return null;
                return {
                  eventsId: createdId,
                  comments,
                  photos: Array.isArray(comment?.photos) ? comment.photos : [],
                  userId,
                };
              })
              .filter(Boolean);
            await Promise.all(commentPayloads.map((data) => createEventComment(data)));
          }
          if (pendingDelays.length) {
            const delayPayloads = pendingDelays
              .map((delay) => {
                const eventDelayCodeId = delay?.eventDelayCodeId ?? delay?.eventDelayCode?.id ?? null;
                if (!eventDelayCodeId) return null;
                return {
                  eventsId: createdId,
                  eventDelayCodeId,
                  delayStartDate: delay?.delayStartDate ?? null,
                  delayEndDate: delay?.delayEndDate ?? null,
                };
              })
              .filter(Boolean);
            await Promise.all(delayPayloads.map((data) => createEventDelay(data)));
          }
        }
      }

      if (resolvedEvent?.id && eventAssignedWorkOrders.length) {
        setEventAssignedWorkOrders((prev) =>
          prev.map((wo) => (wo.eventId ? wo : { ...wo, eventId: resolvedEvent.id })),
        );
        const linkedWorkOrderIds = eventAssignedWorkOrders
          .map((wo) => wo?.id)
          .filter((id) => id !== null && id !== undefined);
        if (linkedWorkOrderIds.length) {
          window.dispatchEvent(
            new CustomEvent('workorders:refresh', {
              detail: { eventId: resolvedEvent.id, workOrderIds: linkedWorkOrderIds },
            }),
          );
        }
        if (typeof onWorkOrderLinked === 'function') {
          onWorkOrderLinked(resolvedEvent.id);
        }
      }

      if (eventTechsEnabled && firstAssignedWorkOrder) {
        const selectedCrew = normalizeCrewArray(eventForm?.crew);
        const existingCrew = normalizeCrewArray(firstAssignedWorkOrder.crew);
        if (!crewsMatch(selectedCrew, existingCrew)) {
          const uniqueCrew = [...new Set(selectedCrew)];
          await updateWorkOrder(firstAssignedWorkOrder.id, { crew: uniqueCrew });
          setEventAssignedWorkOrders((prev) =>
            prev.map((wo) =>
              wo.id === firstAssignedWorkOrder.id ? { ...wo, crew: uniqueCrew } : wo,
            ),
          );
        }
      }

      setEditingEvent(resolvedEvent);
      setEventModalOpen(false);
      await loadShiftContextData(shiftLogForm?.departmentId || resolvedEvent?.departmentId || null);
      if (resolvedEvent?.id) {
        await loadEventWorkOrders(resolvedEvent);
        await loadEventDelays(resolvedEvent.id);
      }
      if (typeof onWorkOrdersUpdated === 'function') {
        onWorkOrdersUpdated();
      }
    } catch (error) {
      console.error('Failed to save event', error);
    }
  }, [
    completeEventWorkOrdersSetting,
    crewsMatch,
    editingEvent,
    eventAssignedWorkOrders,
    eventComments,
    eventDelays,
    eventForm,
    eventTechsEnabled,
    firstAssignedWorkOrder,
    loadEventDelays,
    loadEventWorkOrders,
    loadShiftContextData,
    normalizeCrewArray,
    onWorkOrderLinked,
    onWorkOrdersUpdated,
    shiftLogForm?.departmentId,
    validateEventForm,
  ]);

  const fetchOpenShiftLogs = useCallback(async () => {
    setOpenLogsLoading(true);
    setOpenLogsLoaded(false);
    try {
      const response = await getPaginatedShiftLogs({
        page: 1,
        pageSize: 100,
        completed: 0,
      });
      const logs = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      const filtered = logs.filter((log) => Boolean(log));
      filtered.sort((a, b) => {
        const aDate = new Date(a?.createdAt || 0).getTime();
        const bDate = new Date(b?.createdAt || 0).getTime();
        return bDate - aDate;
      });
      setOpenShiftLogs(filtered);
      return filtered;
    } catch (err) {
      console.error('Failed to load shift logs', err);
      setOpenShiftLogs([]);
      return [];
    } finally {
      setOpenLogsLoading(false);
      setOpenLogsLoaded(true);
    }
  }, []);

  const loadMetadata = useCallback(async () => {
    if (!userProfile) return;
    setLoadingMetadata(true);
    setOpenLogsLoaded(false);
    try {
      const [departmentsResponse, users] = await Promise.all([
        getShiftLogDepartments(),
        getUserses(),
      ]);

      const departmentList = Array.isArray(departmentsResponse)
        ? departmentsResponse
        : Array.isArray(departmentsResponse?.data)
          ? departmentsResponse.data
          : [];

      const departmentLookup = departmentList.reduce((acc, item) => {
        const id = Number(item?.id);
        if (!Number.isInteger(id) || id <= 0) {
          return acc;
        }
        const name = item?.name || `Department ${id}`;
        const parentIdRaw = item?.parentId;
        const parentId =
          parentIdRaw === null || parentIdRaw === undefined
            ? null
            : Number(parentIdRaw);
        acc[id] = {
          id,
          name,
          parentId: Number.isInteger(parentId) ? parentId : null,
        };
        return acc;
      }, {});
      setDepartmentMap(departmentLookup);

      const sortedDepartments = departmentList
        .map((item) => {
          const id = Number(item?.id);
          if (!Number.isInteger(id) || id <= 0) {
            return null;
          }
          const name = item?.name || `Department ${id}`;
          return { id, name };
        })
        .filter(Boolean)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      const departmentOptionList = sortedDepartments.map((dept) => ({
        value: dept.id,
        label: dept.name,
      }));
      setDepartmentOptions(departmentOptionList);
      setDefaultDepartmentId(departmentOptionList.length === 1 ? departmentOptionList[0].value : null);

      const userOptionList = (users || []).map((user) => ({
        value: user.id,
        label: user.name || user.email || `User ${user.id}`,
      }));
      userOptionList.sort((a, b) => a.label.localeCompare(b.label));
      setUserOptions(userOptionList);
      setUserLookup(new Map(userOptionList.map((opt) => [opt.value, opt.label])));

      await fetchOpenShiftLogs();
    } catch (err) {
      console.error('Failed to load shift log metadata', err);
      setDepartmentOptions([]);
      setDepartmentMap({});
      setDefaultDepartmentId(null);
      setUserOptions([]);
      setUserLookup(new Map());
      setOpenShiftLogs([]);
      setOpenLogsLoaded(true);
    } finally {
      setLoadingMetadata(false);
    }
  }, [fetchOpenShiftLogs, userProfile]);

  useEffect(() => {
    if (show && userProfile) {
      setShowSelectionModal(false);
      setShowDepartmentModal(false);
      setShowFormModal(false);
      loadMetadata();
    } else {
      setShowSelectionModal(false);
      setShowDepartmentModal(false);
      setShowFormModal(false);
    }
  }, [loadMetadata, show, userProfile]);

  useEffect(() => {
    if (!showFormModal) return;
    let isMounted = true;
    (async () => {
      try {
        const technicians = await getTechnicians();
        if (!isMounted) return;
        const options = (technicians || []).map((tech) => ({
          value: tech.id,
          label: tech.users?.name || `Technician ${tech.id}`,
        }));
        setCrewOptions(enhanceCrewOptions(options));
      } catch (err) {
        console.error('Failed to load technicians', err);
        if (isMounted) setCrewOptions(enhanceCrewOptions([]));
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [enhanceCrewOptions, showFormModal]);

  const resetManagerState = useCallback(() => {
    setShowSelectionModal(false);
    setShowDepartmentModal(false);
    setShowFormModal(false);
    setShowUnsavedChangesModal(false);
    setActiveFormTab('shiftLog');
    setDepartmentForm({ departmentId: '' });
    setShiftLogForm(createInitialShiftLogForm());
    setShiftLogErrors({});
    setDepartmentErrors({});
    setCrewSelectOption(null);
    setContractorInput('');
    setDownTimeEvents([]);
    setUpcomingEvents([]);
    setShiftLogTasks([]);
    setTasksLoading(false);
    setTaskModalOpen(false);
    setActiveTask(null);
    setTaskForm({ name: '', notes: '' });
    setTaskFormErrors({});
    setTaskSaving(false);
    setUpdatingTaskIds(new Set());
    setIsNewShiftLog(true);
    setActiveShiftLogId(null);
    setIsReadOnly(false);
    setHasPendingChanges(false);
    setPendingAutoSaveNew(false);
    savedSnapshotRef.current = null;
  }, [setDepartmentErrors, setShiftLogErrors]);

  const sortTasks = useCallback((tasks) => {
    const toTimestamp = (value) => {
      if (!value) return 0;
      const time = new Date(value).getTime();
      return Number.isFinite(time) ? time : 0;
    };

    return [...tasks].sort((a, b) => {
      const completeA = Number(a?.complete) === 1 ? 1 : 0;
      const completeB = Number(b?.complete) === 1 ? 1 : 0;
      if (completeA !== completeB) {
        return completeA - completeB;
      }
      const updatedA = toTimestamp(a?.updatedAt);
      const updatedB = toTimestamp(b?.updatedAt);
      if (updatedA !== updatedB) {
        return updatedB - updatedA;
      }
      const createdA = toTimestamp(a?.createdAt);
      const createdB = toTimestamp(b?.createdAt);
      return createdB - createdA;
    });
  }, []);

  const fetchShiftLogTasks = useCallback(async (departmentId) => {
    if (!departmentId || Number.isNaN(departmentId)) {
      setShiftLogTasks([]);
      return;
    }
    setTasksLoading(true);
    try {
      const response = await getShiftLogTasks({ departmentId });
      const tasks = Array.isArray(response) ? response : [];
      setShiftLogTasks(sortTasks(tasks));
    } catch (error) {
      console.error('Failed to load shift log tasks', error);
      setShiftLogTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [sortTasks]);

  useEffect(() => {
    if (!showFormModal) {
      return;
    }
    const rawDepartmentId = shiftLogForm?.departmentId;
    const numericDepartmentId = typeof rawDepartmentId === 'number'
      ? rawDepartmentId
      : Number(rawDepartmentId);
    if (!numericDepartmentId || Number.isNaN(numericDepartmentId)) {
      setShiftLogTasks([]);
      return;
    }
    fetchShiftLogTasks(numericDepartmentId);
  }, [fetchShiftLogTasks, shiftLogForm?.departmentId, showFormModal]);

  const handleOpenTaskModal = useCallback(() => {
    if (isReadOnly) return;
    setActiveTask(null);
    setTaskForm({ name: '', notes: '' });
    setTaskFormErrors({});
    setTaskModalOpen(true);
  }, [isReadOnly]);

  const handleCloseTaskModal = useCallback(() => {
    setTaskModalOpen(false);
    setActiveTask(null);
    setTaskForm({ name: '', notes: '' });
    setTaskFormErrors({});
    setTaskSaving(false);
  }, []);

  const handleEditTaskClick = useCallback((task) => {
    if (!task || isReadOnly) return;
    setActiveTask(task);
    setTaskForm({
      name: task?.name || '',
      notes: task?.notes || '',
    });
    setTaskFormErrors({});
    setTaskModalOpen(true);
  }, [isReadOnly]);

  const handleTaskFormChange = useCallback((field, value) => {
    setTaskForm((prev) => ({ ...prev, [field]: value }));
    setTaskFormErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleSaveTask = useCallback(async (event) => {
    event?.preventDefault?.();
    if (isReadOnly) return;
    const rawDepartmentId = shiftLogForm?.departmentId;
    const numericDepartmentId = typeof rawDepartmentId === 'number'
      ? rawDepartmentId
      : Number(rawDepartmentId);
    if (!numericDepartmentId || Number.isNaN(numericDepartmentId)) {
      setTaskFormErrors((prev) => ({ ...prev, name: 'Select a department before adding tasks.' }));
      return;
    }

    const trimmedName = (taskForm.name || '').trim();
    if (!trimmedName) {
      setTaskFormErrors((prev) => ({ ...prev, name: 'Task name is required.' }));
      return;
    }

    const payloadBase = {
      name: trimmedName,
      notes: (taskForm.notes || '').trim(),
    };

    setTaskSaving(true);
    try {
      if (activeTask?.id) {
        const updated = await updateShiftLogTask(activeTask.id, {
          ...payloadBase,
          updatedBy: userProfile?.id ?? null,
          updatedAt: new Date().toISOString(),
        });
        setShiftLogTasks((prev) => sortTasks(prev.map((task) => (task.id === updated.id ? updated : task))));
      } else {
        const created = await createShiftLogTask({
          ...payloadBase,
          departmentId: numericDepartmentId,
          complete: 0,
          createdBy: userProfile?.id ?? null,
        });
        setShiftLogTasks((prev) => sortTasks([...prev, created]));
      }
      handleCloseTaskModal();
    } catch (error) {
      console.error('Failed to save task', error);
      setTaskSaving(false);
    }
  }, [
    activeTask?.id,
    createShiftLogTask,
    handleCloseTaskModal,
    isReadOnly,
    shiftLogForm?.departmentId,
    sortTasks,
    taskForm.name,
    taskForm.notes,
    updateShiftLogTask,
    userProfile?.id,
  ]);

  const handleDeleteTask = useCallback(async (task) => {
    if (!task || isReadOnly) return;
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    setUpdatingTaskIds((prev) => {
      const next = new Set(prev);
      next.add(task.id);
      return next;
    });
    try {
      await deleteShiftLogTask(task.id);
      setShiftLogTasks((prev) => prev.filter((item) => item.id !== task.id));
    } catch (error) {
      console.error('Failed to delete shift log task', error);
    } finally {
      setUpdatingTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  }, [deleteShiftLogTask, isReadOnly]);

  const handleToggleTaskComplete = useCallback(async (task) => {
    if (!task || isReadOnly) return;
    const newComplete = Number(task.complete) === 1 ? 0 : 1;
    setUpdatingTaskIds((prev) => {
      const next = new Set(prev);
      next.add(task.id);
      return next;
    });
    try {
      const updated = await updateShiftLogTask(task.id, {
        complete: newComplete,
        updatedBy: userProfile?.id ?? null,
        updatedAt: new Date().toISOString(),
      });
      setShiftLogTasks((prev) => sortTasks(prev.map((item) => (item.id === updated.id ? updated : item))));
    } catch (error) {
      console.error('Failed to update task completion', error);
    } finally {
      setUpdatingTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  }, [isReadOnly, sortTasks, updateShiftLogTask, userProfile?.id]);

  const handleCloseAll = useCallback(() => {
    resetManagerState();
    if (onClose) onClose();
  }, [onClose, resetManagerState]);

  const handleAttemptCloseForm = useCallback(() => {
    if (isReadOnly || !hasPendingChanges) {
      handleCloseAll();
      return;
    }
    setShowUnsavedChangesModal(true);
  }, [handleCloseAll, hasPendingChanges, isReadOnly]);

  const handleDiscardUnsavedChanges = useCallback(() => {
    setShowUnsavedChangesModal(false);
    handleCloseAll();
  }, [handleCloseAll]);

  const openNewShiftLogForm = useCallback(
    async (departmentId) => {
      const defaultReportDates = buildDefaultReportDates();
      const defaultShiftName =
        getShiftNameForTime(defaultReportDates.reportStartDateTime) ||
        SHIFT_NAME_OPTIONS[0]?.value ||
        '';
      const initialForm = {
        ...createInitialShiftLogForm(),
        userId: userProfile?.id ?? '',
        departmentId,
        crew: [],
        contractorList: [],
        completed: 0,
        shiftDate: defaultReportDates.reportStartDateDate || getDefaultShiftDate(),
        shiftName: defaultShiftName,
        reportStartDate: defaultReportDates.reportStartDate,
        reportEndDate: defaultReportDates.reportEndDate,
        reportStartDateDate: defaultReportDates.reportStartDateDate,
        reportStartDateTime: defaultReportDates.reportStartDateTime,
        reportEndDateDate: defaultReportDates.reportEndDateDate,
        reportEndDateTime: defaultReportDates.reportEndDateTime,
      };
      setShiftLogForm(initialForm);
      setShiftLogErrors({});
      setCrewSelectOption(null);
      setContractorInput('');
      setIsNewShiftLog(true);
      setShiftNameTouched(false);
      setReportEndDateTouched(false);
      setActiveShiftLogId(null);
      setShowDepartmentModal(false);
      setIsReadOnly(false);
      setActiveFormTab('shiftLog');
      setShowFormModal(true);
      savedSnapshotRef.current = null;
      setPendingAutoSaveNew(true);
      await loadShiftContextData(
        departmentId,
        defaultReportDates.reportStartDate,
        defaultReportDates.reportEndDate,
      );
    },
    [
      buildDefaultReportDates,
      getShiftNameForTime,
      loadShiftContextData,
      setShiftLogErrors,
      userProfile?.id,
    ],
  );

  const handleDepartmentSave = useCallback(async () => {
    if (!validateDepartment()) return;
    const rawDepartmentId = departmentForm.departmentId;
    const numericDepartmentId = Number(rawDepartmentId);
    if (!Number.isInteger(numericDepartmentId) || numericDepartmentId <= 0) {
      setDepartmentErrors({ departmentId: 'Select a department' });
      return;
    }
    if (!Object.prototype.hasOwnProperty.call(departmentMap, numericDepartmentId)) {
      setDepartmentErrors({ departmentId: 'Select a department' });
      return;
    }
    const latest = await fetchOpenShiftLogs();
    const existingOpen = latest.some((log) => Number(log.departmentId) === numericDepartmentId);
    if (existingOpen) {
      setDepartmentErrors({ departmentId: 'Shift Log already open for this department' });
      return;
    }
    openNewShiftLogForm(numericDepartmentId);
  }, [
    departmentForm.departmentId,
    departmentMap,
    fetchOpenShiftLogs,
    openNewShiftLogForm,
    setDepartmentErrors,
    validateDepartment,
  ]);

  const openExistingShiftLog = useCallback(
    async (shiftLogId, options = {}) => {
      setFormLoading(true);
      try {
        const data = await getShiftLogById(shiftLogId);
        const crew = parseJsonArray(data?.crew).map((id) => Number(id)).filter((id) => !Number.isNaN(id));
        const contractorList = parseJsonArray(data?.contractorList).map((name) => String(name || ''));
        const deptId = Number(data?.departmentId) || '';
        const forceReadOnly = Boolean(options?.readOnly);
        const storedDowntime = parseJsonArray(data?.downtimeList);
        const storedUpcoming = parseJsonArray(data?.upcomingEvents);
        const files = parseFileList(data?.files);
        const completedFlag = Number(data?.completed) === 1;
        const readOnlyMode = forceReadOnly || completedFlag;
        const reportStartParts = splitDateTimeParts(data?.reportStartDate ?? null);
        const reportEndParts = splitDateTimeParts(data?.reportEndDate ?? null);
        const formValues = {
          ...createInitialShiftLogForm(),
          userId: data?.userId ?? '',
          departmentId: deptId,
          shiftDate: toDateInputValue(data?.shiftDate),
          shiftName: data?.shiftName || '',
          crew,
          contractorList,
          notes: data?.notes || '',
          safteyNotes: data?.safteyNotes || '',
          completed: completedFlag ? 1 : 0,
          reportStartDate: data?.reportStartDate ?? null,
          reportEndDate: data?.reportEndDate ?? null,
          reportStartDateDate: reportStartParts.date,
          reportStartDateTime: reportStartParts.time,
          reportEndDateDate: reportEndParts.date,
          reportEndDateTime: reportEndParts.time,
          files,
          downtimeList: storedDowntime,
          upcomingEvents: storedUpcoming,
        };
        setShiftLogForm(formValues);
        setShiftLogErrors({});
        setCrewSelectOption(null);
        setContractorInput('');
        setIsNewShiftLog(false);
        setActiveShiftLogId(shiftLogId);
        setShowSelectionModal(false);
        setActiveFormTab('shiftLog');
        setShowFormModal(true);
        setIsReadOnly(readOnlyMode);
        setShiftNameTouched(true);
        setReportEndDateTouched(true);
        setDownTimeEvents(storedDowntime);
        setUpcomingEvents(storedUpcoming);
        updateSavedSnapshot(formValues, storedDowntime, storedUpcoming);
        if (!readOnlyMode) {
          await loadShiftContextData(
            deptId,
            data?.reportStartDate ?? null,
            data?.reportEndDate ?? null,
          );
        }
      } catch (err) {
        console.error('Failed to load shift log', err);
        setIsReadOnly(false);
        setShowSelectionModal(true);
      } finally {
        setFormLoading(false);
      }
    },
    [loadShiftContextData, setShiftLogErrors, updateSavedSnapshot],
  );

  useEffect(() => {
    if (!show) {
      initialShiftLogRef.current = null;
      initialReadOnlyRef.current = false;
      return;
    }

    if (initialShiftLogId === null || initialShiftLogId === undefined) {
      initialShiftLogRef.current = null;
      initialReadOnlyRef.current = false;
      return;
    }

    const sameId = initialShiftLogRef.current === initialShiftLogId;
    const sameReadOnly = initialReadOnlyRef.current === initialReadOnly;
    if (sameId && sameReadOnly) {
      return;
    }

    initialShiftLogRef.current = initialShiftLogId;
    initialReadOnlyRef.current = initialReadOnly;
    openExistingShiftLog(initialShiftLogId, { readOnly: initialReadOnly });
  }, [show, initialShiftLogId, initialReadOnly, openExistingShiftLog]);

  const handleStartNew = useCallback(() => {
    prepareDepartmentSelection();
    setIsReadOnly(false);
    setShowSelectionModal(false);
    setShowDepartmentModal(true);
  }, [prepareDepartmentSelection]);

  useEffect(() => {
    if (!show || !userProfile) return;
    if (initialShiftLogId || initialReadOnly) return;
    if (showFormModal) return;
    if (!openLogsLoaded) return;

    if (openShiftLogs.length > 0) {
      if (!showSelectionModal && !showDepartmentModal) {
        setShowSelectionModal(true);
      }
      return;
    }

    if (!showDepartmentModal) {
      prepareDepartmentSelection();
      setShowSelectionModal(false);
      setShowDepartmentModal(true);
    }
  }, [
    initialReadOnly,
    initialShiftLogId,
    openLogsLoaded,
    openShiftLogs.length,
    prepareDepartmentSelection,
    show,
    showDepartmentModal,
    showFormModal,
    showSelectionModal,
    userProfile,
  ]);

  const currentDepartmentId = useMemo(() => {
    const parseDepartment = (value) => {
      if (value === undefined || value === null) return undefined;
      if (typeof value === 'string' && value.trim() === '') return undefined;
      const numeric = Number(value);
      return Number.isNaN(numeric) ? undefined : numeric;
    };

    const fromShiftLog = parseDepartment(shiftLogForm?.departmentId);
    if (fromShiftLog !== undefined) {
      return fromShiftLog;
    }
    return parseDepartment(departmentForm?.departmentId);
  }, [departmentForm?.departmentId, shiftLogForm?.departmentId]);

  useEffect(() => {
    if (!showFormModal || isReadOnly) {
      return;
    }
    if (!currentDepartmentId) {
      return;
    }
    loadShiftContextData(
      currentDepartmentId,
      shiftLogForm?.reportStartDate ?? null,
      shiftLogForm?.reportEndDate ?? null,
    );
  }, [
    currentDepartmentId,
    isReadOnly,
    loadShiftContextData,
    shiftLogForm?.reportEndDate,
    shiftLogForm?.reportStartDate,
    showFormModal,
  ]);

  const crewIds = useMemo(
    () => (Array.isArray(shiftLogForm?.crew) ? shiftLogForm.crew : []),
    [shiftLogForm?.crew],
  );

  const handleCrewSelectChange = useCallback(
    (option) => {
      if (isReadOnly) return;
      if (option?.value === ADD_CREW_MEMBER_OPTION.value) {
        setCrewSelectOption(null);
        setShowCreateCrewModal(true);
        return;
      }
      setCrewSelectOption(option ?? null);
    },
    [isReadOnly, setCrewSelectOption, setShowCreateCrewModal],
  );

  const handleCrewCreationSuccess = useCallback(
    (result) => {
      const technicianId = result?.technician?.id;
      if (!technicianId) {
        return;
      }
      const label = result?.user?.name || `Technician ${technicianId}`;
      const newOption = { value: technicianId, label };
      setCrewOptions((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        const withoutAdd = existing.filter(
          (opt) => opt && opt.value !== ADD_CREW_MEMBER_OPTION.value && opt.value !== technicianId,
        );
        withoutAdd.push(newOption);
        withoutAdd.sort((a, b) => (a?.label || '').localeCompare(b?.label || ''));
        return [...withoutAdd, ADD_CREW_MEMBER_OPTION];
      });
      setShiftLogForm((prev) => {
        const previous = Array.isArray(prev?.crew) ? prev.crew : [];
        if (previous.includes(technicianId)) {
          return prev;
        }
        return { ...prev, crew: [...previous, technicianId] };
      });
      setCrewSelectOption(null);
    },
    [setCrewOptions, setCrewSelectOption, setShiftLogForm],
  );

  const handleAddCrewMember = useCallback(() => {
    if (isReadOnly || !crewSelectOption) return;
    const technicianId = crewSelectOption.value;
    if (crewIds.includes(technicianId)) return;
    setShiftLogForm((prev) => ({
      ...prev,
      crew: [...(Array.isArray(prev.crew) ? prev.crew : []), technicianId],
    }));
    setCrewSelectOption(null);
  }, [crewIds, crewSelectOption, isReadOnly]);

  const handleRemoveCrewMember = useCallback((technicianId) => {
    if (isReadOnly) return;
    setShiftLogForm((prev) => ({
      ...prev,
      crew: (Array.isArray(prev.crew) ? prev.crew : []).filter((id) => id !== technicianId),
    }));
  }, [isReadOnly]);

  const handleAddContractor = useCallback(() => {
    if (isReadOnly) return;
    const trimmed = contractorInput.trim();
    if (!trimmed) return;
    setShiftLogForm((prev) => ({
      ...prev,
      contractorList: [...(Array.isArray(prev.contractorList) ? prev.contractorList : []), trimmed],
    }));
    setContractorInput('');
  }, [contractorInput, isReadOnly]);

  const handleRemoveContractor = useCallback((name) => {
    if (isReadOnly) return;
    setShiftLogForm((prev) => ({
      ...prev,
      contractorList: (Array.isArray(prev.contractorList) ? prev.contractorList : []).filter((item) => item !== name),
    }));
  }, [isReadOnly]);

  const handleRemoveFile = useCallback(
    (index) => {
      if (isReadOnly) return;
      setShiftLogForm((prev) => {
        const currentFiles = Array.isArray(prev.files) ? prev.files : [];
        const updatedFiles = currentFiles.filter((_, idx) => idx !== index);
        return { ...prev, files: updatedFiles };
      });
    },
    [isReadOnly],
  );

  const preparePayload = useCallback(
    (overrides = {}) => {
      const crew = Array.isArray(shiftLogForm?.crew)
        ? shiftLogForm.crew.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
        : [];
      const contractors = Array.isArray(shiftLogForm?.contractorList)
        ? shiftLogForm.contractorList.map((item) => String(item || ''))
        : [];
      const files = serializeFilesForPayload(shiftLogForm?.files);
      const shouldFinalize = overrides.completed === 1;
      const downtimeList = shouldFinalize
        ? (Array.isArray(downTimeEvents) ? downTimeEvents : [])
        : (Array.isArray(shiftLogForm?.downtimeList) ? shiftLogForm.downtimeList : []);
      const upcomingList = shouldFinalize
        ? (Array.isArray(upcomingEvents) ? upcomingEvents : [])
        : (Array.isArray(shiftLogForm?.upcomingEvents) ? shiftLogForm.upcomingEvents : []);
      const normalizedDowntimeList = downtimeList.map(normalizeShiftLogEventForPayload);
      const normalizedUpcomingList = upcomingList.map(normalizeShiftLogEventForPayload);
      return {
        userId: shiftLogForm?.userId || null,
        departmentId: shiftLogForm?.departmentId || null,
        shiftDate: normalizeDateForPayload(shiftLogForm?.shiftDate),
        shiftName: shiftLogForm?.shiftName ? String(shiftLogForm.shiftName) : null,
        crew,
        contractorList: contractors,
        notes: shiftLogForm?.notes || '',
        safteyNotes: shiftLogForm?.safteyNotes || '',
        reportStartDate: shiftLogForm?.reportStartDate || null,
        reportEndDate: shiftLogForm?.reportEndDate || null,
        files,
        downtimeList: normalizedDowntimeList,
        upcomingEvents: normalizedUpcomingList,
        completed:
          overrides.completed !== undefined
            ? overrides.completed
            : Number(shiftLogForm?.completed) === 1
              ? 1
              : 0,
      };
    },
    [downTimeEvents, isNewShiftLog, shiftLogForm, upcomingEvents],
  );

  const saveShiftLog = useCallback(
    async (options = { completed: undefined, closeAfterSave: true, resetForNew: false }) => {
      if (isReadOnly) return false;
      if (!validateShiftLog()) return false;
      setSaving(true);
      try {
        const payload = preparePayload({ completed: options.completed });
        let departmentId = payload.departmentId;
        let responseData = null;
        let updatedFormState = null;
        if (isNewShiftLog) {
          const created = await createShiftLog(payload);
          responseData = created;
          departmentId = created?.departmentId ?? departmentId;
          setActiveShiftLogId(created?.id ?? null);
          setIsNewShiftLog(false);
        } else if (activeShiftLogId) {
          responseData = await updateShiftLog(activeShiftLogId, payload);
        }

        if (responseData) {
          const parsedFiles = parseFileList(responseData?.files);
          const parsedDowntime = parseJsonArray(responseData?.downtimeList);
          const parsedUpcoming = parseJsonArray(responseData?.upcomingEvents);
          const hasShiftDate =
            responseData && Object.prototype.hasOwnProperty.call(responseData, 'shiftDate');
          const hasShiftName =
            responseData && Object.prototype.hasOwnProperty.call(responseData, 'shiftName');
          setShiftLogForm((prev) => {
            const reportStartDate = responseData?.reportStartDate ?? prev.reportStartDate ?? null;
            const reportEndDate = responseData?.reportEndDate ?? prev.reportEndDate ?? null;
            const reportStartParts = splitDateTimeParts(reportStartDate);
            const reportEndParts = splitDateTimeParts(reportEndDate);
            const nextForm = {
              ...prev,
              shiftDate: hasShiftDate ? toDateInputValue(responseData?.shiftDate) : prev.shiftDate,
              shiftName: hasShiftName
                ? responseData?.shiftName || ''
                : prev.shiftName !== undefined
                  ? prev.shiftName
                  : '',
              reportStartDate,
              reportEndDate,
              reportStartDateDate: reportStartParts.date,
              reportStartDateTime: reportStartParts.time,
              reportEndDateDate: reportEndParts.date,
              reportEndDateTime: reportEndParts.time,
              files: parsedFiles,
              downtimeList: parsedDowntime,
              upcomingEvents: parsedUpcoming,
              completed: Number(responseData?.completed) === 1 ? 1 : prev.completed,
            };
            updatedFormState = nextForm;
            return nextForm;
          });
          if (updatedFormState) {
            updateSavedSnapshot(updatedFormState, parsedDowntime, parsedUpcoming);
          }
          if (options.completed === 1) {
            setDownTimeEvents(parsedDowntime);
            setUpcomingEvents(parsedUpcoming);
          }
        }
        await fetchOpenShiftLogs();

        if (options.resetForNew) {
          const targetDepartment = departmentId || shiftLogForm?.departmentId;
          const defaultReportDates = buildDefaultReportDates();
          const defaultShiftName =
            getShiftNameForTime(defaultReportDates.reportStartDateTime) ||
            SHIFT_NAME_OPTIONS[0]?.value ||
            '';
          const resetForm = {
            ...createInitialShiftLogForm(),
            userId: userProfile?.id ?? '',
            departmentId: targetDepartment,
            completed: 0,
            shiftDate: defaultReportDates.reportStartDateDate || getDefaultShiftDate(),
            shiftName: defaultShiftName,
            reportStartDate: defaultReportDates.reportStartDate,
            reportEndDate: defaultReportDates.reportEndDate,
            reportStartDateDate: defaultReportDates.reportStartDateDate,
            reportStartDateTime: defaultReportDates.reportStartDateTime,
            reportEndDateDate: defaultReportDates.reportEndDateDate,
            reportEndDateTime: defaultReportDates.reportEndDateTime,
          };
          setShiftLogForm(resetForm);
          setShiftLogErrors({});
          setCrewSelectOption(null);
          setContractorInput('');
          setIsNewShiftLog(true);
          setActiveShiftLogId(null);
          setIsReadOnly(false);
          setShiftNameTouched(false);
          setReportEndDateTouched(false);
          savedSnapshotRef.current = null;
          setPendingAutoSaveNew(true);
          await loadShiftContextData(
            targetDepartment,
            defaultReportDates.reportStartDate,
            defaultReportDates.reportEndDate,
          );
        }

        if (options.closeAfterSave) {
          handleCloseAll();
        }
        return true;
      } catch (err) {
        console.error('Failed to save shift log', err);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      activeShiftLogId,
      buildDefaultReportDates,
      fetchOpenShiftLogs,
      getShiftNameForTime,
      handleCloseAll,
      isNewShiftLog,
      isReadOnly,
      loadShiftContextData,
      preparePayload,
      setShiftLogErrors,
      setPendingAutoSaveNew,
      shiftLogForm?.departmentId,
      userProfile?.id,
      updateSavedSnapshot,
      validateShiftLog,
    ],
  );

  useEffect(() => {
    if (!pendingAutoSaveNew) return;
    if (!showFormModal || !isNewShiftLog || saving) return;

    (async () => {
      try {
        await saveShiftLog({ closeAfterSave: false });
      } finally {
        setPendingAutoSaveNew(false);
      }
    })();
  }, [isNewShiftLog, pendingAutoSaveNew, saveShiftLog, saving, showFormModal]);

  const handleSaveAndClose = useCallback(async () => {
    setShowUnsavedChangesModal(false);
    await saveShiftLog({ closeAfterSave: true });
  }, [saveShiftLog]);

  const handleSave = useCallback(() => saveShiftLog({ closeAfterSave: true }), [saveShiftLog]);

  const finaliseAndStartNew = useCallback(
    () => saveShiftLog({ completed: 1, closeAfterSave: false, resetForNew: true }),
    [saveShiftLog],
  );

  const finaliseAndClose = useCallback(
    () => saveShiftLog({ completed: 1, closeAfterSave: true }),
    [saveShiftLog],
  );

  const handleFinaliseClick = useCallback(
    (action) => {
      setPendingFinaliseAction(action);
    },
    [setPendingFinaliseAction],
  );

  const handleFinaliseCancel = useCallback(() => {
    setPendingFinaliseAction(null);
  }, [setPendingFinaliseAction]);

  const handleFinaliseConfirm = useCallback(async () => {
    const action = pendingFinaliseAction;
    if (!action) return;
    setPendingFinaliseAction(null);
    setFinalisingAction(action);
    try {
      if (action === 'startNew') {
        await finaliseAndStartNew();
      } else if (action === 'close') {
        await finaliseAndClose();
      }
    } finally {
      setFinalisingAction(null);
    }
  }, [
    finaliseAndClose,
    finaliseAndStartNew,
    pendingFinaliseAction,
    setPendingFinaliseAction,
    setFinalisingAction,
  ]);

  const renderSelectionModal = () => {
    if (!showSelectionModal) return null;
    return (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="modal-title mb-0">Shift Logs</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAll}
                ></button>
              </div>
            </div>
            <div className="modal-body">
              <p className="fw-semibold mb-12">Select an open shift log, or start a new one.</p>
              {loadingMetadata || openLogsLoading ? (
                <div className="d-flex align-items-center justify-content-center py-24">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading...
                </div>
              ) : openShiftLogs.length ? (
                <div className="table-responsive">
                  <table className="table bordered-table align-middle">
                    <thead>
                      <tr>
                        <th scope="col">Department</th>
                        <th scope="col">Supervisor</th>
                        <th scope="col">Report Start/End</th>
                        <th scope="col">Shift Name</th>
                        <th scope="col">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openShiftLogs.map((log) => (
                        <tr
                          key={log.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openExistingShiftLog(log.id)}
                        >
                          <td>
                            {departmentMap[log.departmentId]?.name || `Department ${log.departmentId}`}
                          </td>
                          <td>{userLookup.get(log.userId) || `User ${log.userId}`}</td>
                          <td>{formatReportRange(log.reportStartDate, log.reportEndDate)}</td>
                          <td>{log.shiftName || '—'}</td>
                          <td>{formatDateTime(log.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="alert alert-info" role="alert">
                  No open shift logs for your departments.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseAll}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleStartNew}
              // disabled={!departmentOptions.length}
              >
                Start New Shift Log
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDepartmentModal = () => {
    if (!showDepartmentModal) return null;
    return (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Start New Shift Log</h5>
              <button type="button" className="btn-close" onClick={handleCloseAll}></button>
            </div>
            <div className="modal-body">
              <div className="row g-3">{departmentFields.map(renderDepartmentField)}</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseAll}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleDepartmentSave}>
                Start New Shift Log
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCrewSection = () => (
    <div className="col-md-12">
      <div className="d-flex align-items-center justify-content-between mb-12">
        <h6 className="mt-8 mb-0 text-lg">Crew</h6>
        <div className="d-flex align-items-center gap-2" style={{ minWidth: '280px' }}>
          <div style={{ width: '300px' }}>
            <Select
              classNamePrefix="react-select"
              value={crewSelectOption}
              onChange={handleCrewSelectChange}
              options={crewOptions}
              placeholder="Select crew member"
              isClearable
              isDisabled={isReadOnly}
            />
          </div>
          <button
            className="btn btn-outline-primary btn-sm"
            type="button"
            onClick={handleAddCrewMember}
            disabled={
              isReadOnly ||
              !crewSelectOption ||
              crewIds.includes(crewSelectOption.value)
            }
          >
            <Icon
              icon="ic:baseline-plus"
              className="me-1"
              style={{ position: "relative", top: "-4px" }}
            /> Add
          </button>
        </div>
      </div>
      {crewDisplay.length ? (
        <div className="table-responsive">
          <table className="table bordered-table table-sm align-middle">
            <thead className="d-none">
              <tr>
                <th scope="col">Crew Member</th>
                <th scope="col" className="text-end">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {crewDisplay.map((crew) => (
                <tr key={crew.id}>
                  <td>{crew.label}</td>
                  <td className="text-end">
                    {!isReadOnly && (
                      <button
                        className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        type="button"
                        onClick={() => handleRemoveCrewMember(crew.id)}
                      >
                        <Icon icon="ic:baseline-delete-outline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted fst-italic">No crew members added.</p>
      )}
    </div>
  );

  const contractorList = Array.isArray(shiftLogForm?.contractorList) ? shiftLogForm.contractorList : [];

  const renderContractorSection = () => (
    <div className="col-md-12">
      <div className="d-flex align-items-center justify-content-between mb-12">
        <h6 className="mt-8 mb-0 text-lg">Contractors</h6>
        <div className="d-flex align-items-center gap-2" style={{ minWidth: '280px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Contractor name"
            value={contractorInput}
            style={{ width: "300px" }}
            onChange={(e) => setContractorInput(e.target.value)}
            disabled={isReadOnly}
          />
          <button
            className="btn btn-outline-primary btn-sm"
            type="button"
            onClick={handleAddContractor}
            disabled={isReadOnly}
          >
            <Icon
              icon="ic:baseline-plus"
              className="me-1"
              style={{ position: "relative", top: "-4px" }}
            /> Add
          </button>
        </div>

      </div>
      {contractorList.length ? (
        <ul className="list-group">
          {contractorList.map((name) => (
            <li key={name} className="list-group-item d-flex justify-content-between align-items-center">
              <span>{name}</span>
              {!isReadOnly && (
                <button
                  className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                  type="button"
                  onClick={() => handleRemoveContractor(name)}
                >
                  <Icon icon="ic:baseline-delete-outline" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted fst-italic">No contractors added.</p>
      )}
    </div>
  );

  const renderDowntimeCommentsTab = () => {
    if (!downtimeEventsWithIds.length) {
      return (
        <div
          className="text-muted fst-italic d-flex align-items-center justify-content-center"
          style={{ height: 'calc(100vh - 320px)' }}
        >
          No in-progress or recently completed events to display.
        </div>
      );
    }

    if (downtimeCommentsLoading) {
      return (
        <div
          className="d-flex align-items-center justify-content-center gap-2"
          style={{ height: 'calc(100vh - 320px)' }}
        >
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Loading event comments...
        </div>
      );
    }

    return (
      <div
        className="d-flex flex-column gap-3"
        style={{
          height: 'calc(100vh - 320px)',
          overflow: 'auto',
        }}
      >
        <div className="mt-4" style={{ justifyContent: 'center', display: 'grid', width: '100%' }}>
          <div className="d-flex justify-content-end gap-2" style={{ width: '800px' }}>
            <button
              type="button"
              className="btn btn-secondary-outline btn-sm"
              onClick={handleExpandAllDowntimeComments}
              disabled={!eventsWithDowntimeComments.length}
            >
              Expand All
            </button>
            <button
              type="button"
              className="btn btn-secondary-outline btn-sm"
              onClick={() =>
                handleCollapseAllDowntimeComments(
                  eventsWithDowntimeComments.map((event) => event.eventId),
                )
              }
              disabled={!eventsWithDowntimeComments.length}
            >
              Collapse All
            </button>
          </div>
          {downtimeCommentsError && (
            <div className="alert alert-danger mb-0" role="alert">
              {downtimeCommentsError}
            </div>
          )}
          {eventsWithDowntimeComments.length ? (
            eventsWithDowntimeComments.map(({ eventId, eventName, assetLabel, comments }) => {
              const isCollapsed = collapsedCommentEventIds.has(eventId);
              return (
                <div key={eventId} className="border rounded">
                  <div className="p-3 border-bottom bg-light d-flex align-items-center justify-content-between gap-3">
                    <div className="d-flex flex-column">
                      <span className="fw-semibold">{eventName}</span>
                      {assetLabel && <span className="text-muted small">{assetLabel}</span>}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none text-dark d-flex align-items-center gap-1"
                      onClick={() => toggleDowntimeCommentSection(eventId)}
                      aria-expanded={!isCollapsed}
                    >
                      <Icon icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-down'} className="icon text-xl" />
                    </button>
                  </div>
                  {!isCollapsed && (
                    <div className="p-3">
                      {comments.map((comment, commentIndex) => {
                        console.log('comment', comment);
                        const initials =
                          comment?.userInitials ||
                          (comment?.userName || '')
                            .split(' ')
                            .map((part) => part?.[0] || '')
                            .join('')
                            .slice(0, 2)
                            .toUpperCase();
                        return (
                          <div
                            key={`${eventId}-${comment?.id ?? commentIndex}`}
                            className={`d-flex align-items-start gap-3 ${commentIndex < comments.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}`}
                          >
                            <div className="flex-shrink-0">
                              {comment?.userImage ? (
                                <img
                                  src={comment.userImage}
                                  alt={comment?.userName || 'User avatar'}
                                  className="rounded-circle border"
                                  style={{ width: 40, height: 40, objectFit: 'cover' }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle bg-primary text-white fw-semibold d-flex align-items-center justify-content-center"
                                  style={{ width: 40, height: 40 }}
                                >
                                  {initials || '—'}
                                </div>
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold">{comment?.userName || 'Unknown User'}</span>
                                <span className="text-muted small">{formatTimeSincePosting(comment?.timestamp)}{comment?.timestamp}</span>
                              </div>
                              <div style={{ whiteSpace: 'pre-line' }}>{comment?.comment}</div>
                              {Array.isArray(comment?.photos) && comment.photos.length > 0 && (
                                <div className="d-flex flex-wrap gap-2 mt-8">
                                  {comment.photos.map((photo, photoIndex) => (
                                    <a
                                      key={`${eventId}-${comment?.id ?? commentIndex}-photo-${photoIndex}`}
                                      href={photo}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="d-inline-flex"
                                    >
                                      <img
                                        src={photo}
                                        alt={`Comment photo ${photoIndex + 1}`}
                                        className="rounded border"
                                        style={{ width: 64, height: 64, objectFit: 'cover' }}
                                      />
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="border rounded py-5 text-center text-muted fst-italic">
              No event comments recorded for these events.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTasksSection = () => {
    const rawDepartmentId = shiftLogForm?.departmentId;
    const numericDepartmentId = typeof rawDepartmentId === 'number'
      ? rawDepartmentId
      : Number(rawDepartmentId);
    const canModifyTasks = !isReadOnly && numericDepartmentId && !Number.isNaN(numericDepartmentId);
    return (
      <div className="col-md-12">
        <div className="d-flex align-items-center justify-content-between mb-12">
          <h6 className="mt-8 mb-0 text-lg">Tasks and Projects</h6>
          <button
            className="btn btn-outline-primary btn-sm"
            type="button"
            onClick={handleOpenTaskModal}
            disabled={!canModifyTasks}
          >
            <Icon
              icon="ic:baseline-plus"
              className="me-1"
              style={{ position: 'relative', top: '-4px' }}
            />
            Add Task
          </button>
        </div>
        {tasksLoading ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Loading tasks...
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table bordered-table">
              <thead>
                <tr>
                  <th scope="col">Task Name</th>
                  <th scope="col">Description</th>
                  <th scope="col">Complete</th>
                  <th scope="col" className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shiftLogTasks.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-muted fst-italic">
                      No tasks recorded.
                    </td>
                  </tr>
                ) : (
                  shiftLogTasks.map((task) => {
                    const isComplete = Number(task?.complete) === 1;
                    const isUpdating = updatingTaskIds.has(task.id);
                    return (
                      <tr key={task.id}>
                        <td>
                          <span style={{ textDecoration: isComplete ? 'line-through' : 'none', maxWidth: '300px', }}>
                            {task?.name || '—'}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              textDecoration: isComplete ? 'line-through' : 'none',
                              display: 'inline-block',
                              maxWidth: '850px',
                              whiteSpace: 'normal',
                              overflowWrap: 'break-word'
                            }}
                          >
                            {task?.notes || '-'}
                          </span>
                        </td>

                        <td>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isComplete}
                              onChange={() => handleToggleTaskComplete(task)}
                              disabled={isReadOnly || isUpdating}
                            />
                          </div>
                        </td>
                        <td className="text-end">
                          <button
                            className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                            type="button"
                            onClick={() => handleEditTaskClick(task)}
                            disabled={isReadOnly || isUpdating}
                          >
                            <Icon icon="ic:baseline-edit" />
                          </button>
                          <button
                            className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            type="button"
                            onClick={() => handleDeleteTask(task)}
                            disabled={isReadOnly || isUpdating}
                          >
                            <Icon icon="ic:baseline-delete-outline" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderShiftTable = (title, rows, columns, emptyMessage) => (
    <div className="col-md-12">
      <h6 className="mt-8 mb-3 text-lg">{title}</h6>
      {rows.length ? (
        <div className="table-responsive">
          <table className="table bordered-table table-sm align-middle">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} scope="col">{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || row.key || row.name}>
                  {columns.map((col) => (
                    <td key={col.key}>{col.render(row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted fst-italic">{emptyMessage}</p>
      )}
    </div>
  );

  const downTimeColumns = useMemo(
    () => [
      { key: 'name', label: 'Event', render: (event) => event?.name || '—' },
      { key: 'assetNumber', label: 'Asset #', render: (event) => event?.asset?.assetNumber || '—' },
      {
        key: 'assetDescription',
        label: 'Asset Description',
        render: (event) => event?.asset?.assetDescription || '—',
      },
      { key: 'system', label: 'System', render: (event) => event?.system?.name || '—' },
      { key: 'startDate', label: 'Start Date', render: (event) => formatDateTime(event?.startDate) },
      {
        key: 'plannedEndDate',
        label: 'Planned End Date',
        render: (event) => formatDateTime(event?.plannedEndDate),
      },
      { key: 'delays', label: 'Delays', render: (event) => buildDelaySummary(event) },
      {
        key: 'actions',
        label: 'Actions',
        render: (event) => (
          <div className="text-end">
            <button
              className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
              type="button"
              onClick={() => handleEditEventClick(event)}
              disabled={isReadOnly}
            >
              <Icon icon="ic:baseline-edit" />
            </button>
          </div>
        ),
      },
    ],
    [handleEditEventClick, isReadOnly],
  );

  const upcomingColumns = [
    { key: 'name', label: 'Event', render: (event) => event?.name || '—' },
    { key: 'assetNumber', label: 'Asset #', render: (event) => event?.asset?.assetNumber || '—' },
    { key: 'assetDescription', label: 'Asset Description', render: (event) => event?.asset?.assetDescription || '—' },
    { key: 'system', label: 'System', render: (event) => event?.system?.name || '—' },
    { key: 'plannedStartDate', label: 'Planned Start', render: (event) => formatDateTime(event?.plannedStartDate) },
    { key: 'plannedEndDate', label: 'Planned End', render: (event) => formatDateTime(event?.plannedEndDate) },
  ];

  const filesList = useMemo(
    () => (Array.isArray(shiftLogForm?.files) ? shiftLogForm.files : []),
    [shiftLogForm?.files],
  );

  const renderFileTable = () => (
    <div className="col-md-12">
      <h6 className="mt-8 mb-12 text-lg">Attachments</h6>
      {filesList.length ? (
        <div className="table-responsive">
          <table className="table bordered-table table-sm align-middle">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Filename</th>
                <th scope="col">Size</th>
                <th scope="col" className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filesList.map((file, index) => {
                const url = buildFileUrl(file);
                const displayName = file?.name || file?.filename || file?.key || `File ${index + 1}`;
                const filename = file?.filename || file?.key || displayName;
                const size = typeof file?.size === 'number' ? file.size : null;
                return (
                  <tr key={`${filename}-${index}`}>
                    <td>
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {displayName}
                        </a>
                      ) : (
                        displayName
                      )}
                    </td>
                    <td>
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {filename}
                        </a>
                      ) : (
                        filename
                      )}
                    </td>
                    <td>{size !== null ? `${(size / 1024 / 1024).toFixed(2)} MB` : '—'}</td>
                    <td className="text-end">
                      {!isReadOnly && (
                        <button
                          className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                        >
                          <Icon icon="ic:baseline-delete-outline" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted fst-italic">No files uploaded.</p>
      )}
    </div>
  );

  const renderShiftLogForm = () => {
    if (!showFormModal) return null;
    const formFields = shiftLogFields.filter((field) =>
      [
        'userId',
        'departmentId',
        'shiftName',
        'reportStartDateDate',
        'reportStartDateTime',
        'reportEndDateDate',
        'reportEndDateTime',
      ].includes(field.field),
    );
    const noteFields = shiftLogFields.filter((field) => ['notes', 'safteyNotes'].includes(field.field));
    const attachmentsField = shiftLogFields.find((field) => field.field === 'files');

    return (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-xxl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h5 className="modal-title mb-0">Shift Log</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleAttemptCloseForm}
                ></button>
              </div>
            </div>
            <div className="modal-body">
              <div className="d-flex justify-content-between align-items-center mb-24">
                <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills mb-0">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeFormTab === 'shiftLog' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setActiveFormTab('shiftLog')}
                    >
                      General
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeFormTab === 'shiftEvents' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setActiveFormTab('shiftEvents')}
                    >
                      Shift Events
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeFormTab === 'downtimeComments' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setActiveFormTab('downtimeComments')}
                    >
                      Comments
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeFormTab === 'documents' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setActiveFormTab('documents')}
                    >
                      Documents
                    </button>
                  </li>
                </ul>

                <div className="d-flex gap-2">
                  {!isReadOnly && (
                    <>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        type="button"
                        onClick={() => handleFinaliseClick('startNew')}
                        disabled={saving}
                      >
                        {saving && finalisingAction === 'startNew' ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-8"
                              role="status"
                              aria-hidden="true"
                            />
                            Finalising Shift Report
                          </>
                        ) : (
                          'Finalise and Start New'
                        )}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        type="button"
                        onClick={() => handleFinaliseClick('close')}
                        disabled={saving}
                      >
                        {saving && finalisingAction === 'close' ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-8"
                              role="status"
                              aria-hidden="true"
                            />
                            Finalising Shift Report
                          </>
                        ) : (
                          'Finalise and Close'
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {formLoading ? (
                <div
                  className="d-flex justify-content-center py-24"
                  style={{ height: 'calc(100vh - 320px)', overflow: 'auto' }}
                >
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading...
                </div>
              ) : activeFormTab === 'shiftLog' ? (
                <div className="row g-3 mb-24" style={{ height: 'calc(100vh - 320px)', overflow: 'auto', alignContent: "start" }}>
                  {formFields.map(renderShiftLogField)}
                  {noteFields.map(renderShiftLogField)}
                  {renderTasksSection()}
                  {renderCrewSection()}
                  {renderContractorSection()}
                </div>
              ) : activeFormTab === 'shiftEvents' ? (
                <>
                  <div className="d-flex justify-content-end mb-12">
                    {!isReadOnly && (
                      <button
                        className="btn btn-primary btn-sm"
                        type="button"
                        onClick={handleAddEventClick}
                      >
                        <Icon icon='ic:baseline-plus' className='icon text-xl' />
                        Add Event
                      </button>
                    )}
                  </div>
                  <div className="row g-3" style={{ height: 'calc(100vh - 320px)', overflow: 'auto', alignContent: "start" }}>
                    {renderShiftTable(
                      'In Progress Events',
                      inProgressEvents,
                      downTimeColumns,
                      'No in-progress events found.'
                    )}
                    {renderShiftTable(
                      'Recently Completed Events',
                      recentlyCompletedEvents,
                      downTimeColumns,
                      `No recently completed events near ${formattedReportStartDate}.`
                    )}
                    {renderShiftTable(
                      'Upcoming Events',
                      upcomingEvents,
                      upcomingColumns,
                      'No upcoming events in the next 24 hours.'
                    )}
                  </div>
                </>
              ) : activeFormTab === 'downtimeComments' ? (
                renderDowntimeCommentsTab()
              ) : (
                <div className="row g-3" style={{ height: 'calc(100vh - 300px)', overflow: 'auto', alignContent: "start" }}>
                  {attachmentsField && renderShiftLogField(attachmentsField)}
                  {renderFileTable()}
                </div>
              )}
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={handleCloseAll}
                >
                  Close
                </button>
                {!isReadOnly && (
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUnsavedChangesModal = () => {
    if (!showUnsavedChangesModal) return null;
    return (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Unsaved Changes</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowUnsavedChangesModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <p className="mb-0">There are unsaved changes. Do you want save the Shift Log?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleDiscardUnsavedChanges}>
                No
              </button>
              <button className="btn btn-primary" onClick={handleSaveAndClose}>
                Yes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFinaliseModal = () => {
    if (!pendingFinaliseAction) return null;
    return (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Finalise Shift Report</h5>
              <button type="button" className="btn-close" onClick={handleFinaliseCancel}></button>
            </div>
            <div className="modal-body">
              <p>
                This will close the shift report and end the shift. Once closed, the report cannot be
                edited. Do you want to finalise the shift report for {formattedReportStartDate}?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" type="button" onClick={handleFinaliseCancel}>
                Cancel
              </button>
              <button className="btn btn-danger" type="button" onClick={handleFinaliseConfirm}>
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTaskModal = () => {
    if (!taskModalOpen) return null;
    const modalTitle = activeTask?.id ? 'Edit Task' : 'Add Task';
    return (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{modalTitle}</h5>
              <button type="button" className="btn-close" onClick={handleCloseTaskModal}></button>
            </div>
            <form onSubmit={handleSaveTask}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label" htmlFor="task-name">
                    Task Name
                  </label>
                  <input
                    id="task-name"
                    type="text"
                    className={`form-control ${taskFormErrors.name ? 'is-invalid' : ''}`}
                    value={taskForm.name}
                    onChange={(e) => handleTaskFormChange('name', e.target.value)}
                    disabled={taskSaving}
                  />
                  {taskFormErrors.name && (
                    <div className="invalid-feedback">{taskFormErrors.name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="task-notes">
                    Description
                  </label>
                  <textarea
                    id="task-notes"
                    className="form-control"
                    rows={4}
                    value={taskForm.notes}
                    onChange={(e) => handleTaskFormChange('notes', e.target.value)}
                    disabled={taskSaving}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={handleCloseTaskModal}
                  disabled={taskSaving}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" type="submit" disabled={taskSaving}>
                  {taskSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <CreateDisplayOnlyUserModal
        show={showCreateCrewModal}
        onClose={() => setShowCreateCrewModal(false)}
        onSuccess={handleCrewCreationSuccess}
        departmentId={currentDepartmentId}
      />
      {renderSelectionModal()}
      {renderDepartmentModal()}
      {renderShiftLogForm()}
      {renderUnsavedChangesModal()}
      {renderTaskModal()}
      {showDelayModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingDelay ? 'Edit Delay' : 'Add Delay'}</h5>
                <button className="btn-close" onClick={() => setShowDelayModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">{delayFields.map(renderDelayField)}</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDelayModal(false)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleDelaySave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEventAssignModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Assign Work Orders</h5>
                <button className="btn-close" onClick={() => setShowEventAssignModal(false)} />
              </div>
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <input
                    type="text"
                    className="form-control w-auto"
                    placeholder="Search..."
                    value={eventAssignSearch}
                    onChange={(e) => setEventAssignSearch(e.target.value)}
                  />
                  <div className="d-flex gap-2">
                    <button className="btn btn-primary btn-sm" onClick={handleEventSelectAllAssign}>
                      Select All
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={handleEventDeselectAllAssign}>
                      Deselect All
                    </button>
                  </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: "calc(100vh - 500px)" }}>
                  <table className="table bordered-table mb-0 w-100 rounded-0">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Number</th>
                        <th>Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEventAssignOptions.map((wo) => (
                        <tr key={wo.id}>
                          <td>
                            <div className="form-check style-check d-flex justify-content-center">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={eventAssignSelected.includes(wo.id)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setEventAssignSelected((prev) =>
                                    checked ? [...prev, wo.id] : prev.filter((id) => id !== wo.id),
                                  );
                                }}
                              />
                            </div>
                          </td>
                          <td>{wo.workOrderNumber}</td>
                          <td>{wo.workOrderTitle}</td>
                        </tr>
                      ))}
                      {filteredEventAssignOptions.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-center py-2">
                            No Work Orders Available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEventAssignModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleEventAssignSelected}>
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <EventEditModal
        show={eventModalOpen}
        onClose={handleCloseEventModal}
        onSave={handleEventModalSave}
        editingEvent={editingEvent}
        newEvent={eventForm}
        setNewEvent={setEventForm}
        newEventErrors={eventFormErrors}
        statusOptions={eventStatusOptions}
        eventTypeOptions={eventTypeOptions}
        eventResponsibilityOptions={eventResponsibilityOptions}
        showEventResponsibility
        assetOptions={eventAssetOptions}
        handleAssetSearch={handleEventAssetSearch}
        assignedWorkOrders={eventAssignedWorkOrders}
        workOrderStatuses={eventWorkOrderStatuses}
        onCompleteAllWork={eventAssignedWorkOrders.length ? handleCompleteAllWork : null}
        onOpenAssignModal={!isReadOnly ? openEventAssignModal : null}
        onWorkOrderStatusChange={eventAssignedWorkOrders.length ? handleWorkOrderStatusChange : null}
        onDeleteWorkOrder={!isReadOnly ? handleDeleteEventWorkOrder : null}
        onEditWorkOrder={null}
        eventDelays={eventDelays}
        onAddDelay={handleAddDelay}
        onEditDelay={handleEditDelay}
        onDeleteDelay={handleDeleteDelay}
        formatDateTime={formatDateTime}
        eventComments={eventComments}
        onEventCommentsChange={setEventComments}
      />
      {renderFinaliseModal()}
    </>
  );
};

export default ShiftLogManager;
